import { generateClient } from '@aws-amplify/api';
import axios from 'axios';
import { useDispatch, useSelector, useStore } from 'react-redux';
import sendEmail from '../Services/sendEmail';
import sendSms from '../Services/sendSMS';
import {
  BookingOrigin,
  BookingPendingFilter,
  BookingSpecialNeedsFilter,
  BookingStatus,
  BookingsPaymentStatus,
  LambdaFunctions,
  PaymentLinkOrigin,
} from '../constants/enums';
import {
  createBooking,
  createTimeline,
  deleteBooking,
  updateBooking,
} from '../graphql/mutations';
import {
  bookingByEventId,
  bookingByGuestId,
  bookingByMainBookingId,
  bookingByMainGuestId,
  getBooking,
  getEventTicket,
  listBookings,
} from '../graphql/queries';
import { onCreateBooking } from '../graphql/subscriptions';
import {
  Booking,
  CreateBookingInput,
  Guest,
  ListBookingsQueryVariables,
  ModelBookingFilterInput,
  UpdateBookingInput,
} from '../models/GQL_API';
import {
  invokeLambda,
  phoneVariants,
  toAsciiDigits,
  toPhoneSearchText,
} from '../helpers/utils';
import {
  bookingByEventIdExport,
  bookingByEventIdListing,
  bookingByEventIdPreview,
  bookingIdsByEventId,
  bookingsByEventAndStatusExport,
  bookingsByEventAndStatusListing,
  invitationByEventIdExport,
} from '../constants/customQueries';
import { toDayRangeIso } from '../helpers/dateRange';
import { patchListResultRows } from '../helpers/listResultCache';
import { HeadCell } from '../models/dataTable';
import {
  setListing,
  setNextToken,
  setRegisteredFilter,
  setSelected,
} from '../store/ducks/booking';
import { Invitation, ModelSortDirection } from './../models/GQL_API';
import {
  BookingBulkTrashVariables,
  BookingGetVariables,
  BookingListingVariables,
  BookingUpdateVariables,
  CreateVariables,
  GeneralFilters,
} from './../models/app';
import useApp from './useApp';
import useGuest from './useGuest';
import { buildPaymentPageUrl } from '../utils/paymentLinkParams';
import { PUBLIC_SITE_URL } from '../constants/site';
import {
  WAVE_ENDED_APPROVAL_MESSAGE,
  capExpiryToWaveEnd,
  getAutoRejectWaveEndTs,
} from '../helpers/waveDeadline';

const client = generateClient();
const DEFAULT_PAYMENT_EXPIRATION_HOURS = 48;

// Export-only libraries, loaded on first use so they stay out of the main
// bundle (useBooking is imported by most pages).
const loadExcelJS = async (): Promise<any> => {
  const mod: any = await import('exceljs');
  return mod.default ?? mod;
};
const loadSaveAs = async (): Promise<(data: Blob, filename?: string) => void> => {
  const mod: any = await import('file-saver');
  return mod.saveAs ?? mod.default?.saveAs ?? mod.default;
};
const loadQRCode = async (): Promise<any> => {
  const mod: any = await import('qrcode');
  return mod.default ?? mod;
};

/**
 * Serve single-`status` listings from the bookingsByEventAndStatus GSI
 * (amplify schema: Booking.bookingEventId @index sortKeyFields ["status"]).
 * Set to false to always use bookingByEventId.
 */
const USE_STATUS_INDEX = true;
// Flipped to false (once per page load) if the API doesn't expose the new
// query yet, so the frontend can ship before `amplify push`.
let statusIndexAvailable = true;

/**
 * Server page sizes for the Bookings list. The first request of a listing
 * evaluates fewer items so the first rows render fast; follow-up pages
 * (Next / filling a sparse filter) use the large page. DynamoDB also caps
 * every page at 1MB, so the large value is effectively "one full page".
 */
const BOOKINGS_FIRST_PAGE_LIMIT = 300;
const BOOKINGS_NEXT_PAGE_LIMIT = 1000;

interface BookingListFilterOptions {
  generalFilters?: Partial<GeneralFilters>;
  searchText?: string;
  /** The list's status tab (Redux bookings.statusFilter). */
  statusFilter?: string;
  isMainGuest?: boolean;
  status?: string;
  overallStatus?: string;
  mainGuestID?: string;
  guestID?: string;
  eventID?: string;
}

/**
 * The Bookings list's filter (shared by the list fetch and the exports, so
 * an export contains what the list shows): registered / special needs /
 * paid / origin / search / tab / ticket / wave, plus the createdAt range of
 * the date filter (whole local days). Never mutates its inputs.
 */
const buildBookingListFilter = (
  opts: BookingListFilterOptions,
): {
  filter: ModelBookingFilterInput;
  createdAtFilter?: { between: [string, string] };
} => {
  const {
    generalFilters,
    searchText,
    statusFilter,
    isMainGuest,
    status,
    overallStatus,
    mainGuestID,
    guestID,
    eventID,
  } = opts;
  const filter: ModelBookingFilterInput = {
    deleted: { eq: '0' },
    overallStatus: { ne: BookingStatus.TEST },
  };
  if (generalFilters?.bookingsRegisteredFilter) {
    if (
      generalFilters?.bookingsRegisteredFilter ===
      BookingPendingFilter.REGISTERED
    ) {
      filter.bookingGuestId = { ne: '' };
      filter.hasNotRegisteredGuest = { eq: false };
    }
  }
  if (generalFilters?.bookingSpecialNeedsFilter) {
    if (
      generalFilters?.bookingSpecialNeedsFilter ===
      BookingSpecialNeedsFilter.Yes
    ) {
      filter.specialNeed = { eq: false };
    }
  }
  if (generalFilters?.bookingsPaymentStatus) {
    switch (generalFilters.bookingsPaymentStatus) {
      case BookingsPaymentStatus.PAID:
        filter.isPaid = { eq: true };
        break;
      case BookingsPaymentStatus.NOT_PAID:
        filter.isPaid = { eq: false };
        break;
      default:
        break;
    }
  }
  if (generalFilters?.bookingsOrigin === BookingOrigin.HOST_APP) {
    filter.origin = { eq: BookingOrigin.HOST_APP };
  } else if (generalFilters?.bookingsOrigin === BookingOrigin.WEBSITE) {
    filter.origin = { ne: BookingOrigin.HOST_APP };
  }
  const or: ModelBookingFilterInput[] = [];
  if (searchText && searchText.length > 0) {
    if (/[a-zA-Z]/.test(searchText)) {
      or.push({
        guestName: { contains: searchText.toLowerCase() },
      });
    } else if (/[0-9]/.test(toAsciiDigits(searchText))) {
      // Arabic-Indic / Persian digits and any stored phone format
      or.push({ phone_number: { contains: toPhoneSearchText(searchText) } });
    }
  }
  if (or.length > 0) filter.or = or;

  if (isMainGuest !== undefined && statusFilter !== BookingStatus.TRANSFERED) {
    filter.isMainGuest = { eq: isMainGuest };
  }
  if (mainGuestID) filter.bookingMainGuestId = { eq: mainGuestID };
  if (guestID) filter.bookingGuestId = { eq: guestID };
  if (status) filter.status = { eq: status };
  if (statusFilter === BookingStatus.TRANSFERED)
    filter.status = { eq: BookingStatus.TRANSFERED };
  if (statusFilter === BookingStatus.CANCELLED) {
    filter.status = { eq: BookingStatus.CANCELLED };
    filter.deleted = { eq: '1' };
  }
  if (statusFilter === BookingStatus.REJECTED) {
    filter.status = { eq: BookingStatus.REJECTED };
    filter.deleted = { eq: '0' };
  }
  if (eventID) filter.bookingEventId = { eq: eventID };
  if (
    overallStatus &&
    statusFilter !== BookingStatus.TRANSFERED &&
    statusFilter !== BookingStatus.CANCELLED &&
    statusFilter !== BookingStatus.REJECTED
  ) {
    filter.overallStatus = { eq: overallStatus };
    filter.isPendingTransfer = { ne: true };
  }
  if (!statusFilter || statusFilter === BookingStatus.ALL) {
    filter.isPendingTransfer = { ne: true };
  }

  if (generalFilters?.ticket)
    filter.bookingEventTicketId = { eq: generalFilters.ticket };
  if (generalFilters?.wave) filter.waveId = { eq: generalFilters.wave };

  const createdAtRange = toDayRangeIso(
    generalFilters?.fromDate,
    generalFilters?.toDate,
  );
  return {
    filter,
    createdAtFilter: createdAtRange ? { between: createdAtRange } : undefined,
  };
};

const isUnknownStatusIndexError = (err: any): boolean => {
  const errors: any[] = Array.isArray(err?.errors) ? err.errors : [err];
  return errors.some((e) => {
    const msg = String(e?.message ?? e ?? '');
    return (
      msg.includes('bookingsByEventAndStatus') &&
      /FieldUndefined|undefined|Validation error|Unknown/i.test(msg)
    );
  });
};

const buildPaymentExpiryIso = (baseIso?: string) => {
  const baseDate = baseIso ? new Date(baseIso) : new Date();
  const next = new Date(baseDate.getTime());
  next.setHours(next.getHours() + DEFAULT_PAYMENT_EXPIRATION_HOURS);
  return next.toISOString();
};

const buildPaymentExpiryIsoFromDays = (days: number, baseIso?: string) => {
  const safeDays = Number.isFinite(days) ? Math.max(0, days) : 0;
  const baseDate = baseIso ? new Date(baseIso) : new Date();
  const next = new Date(baseDate.getTime());
  next.setDate(next.getDate() + safeDays);
  return next.toISOString();
};

const buildPaymentExpiryIsoFromHours = (hours: number, baseIso?: string) => {
  const safeHours = Number.isFinite(hours) ? Math.max(0, hours) : 0;
  const baseDate = baseIso ? new Date(baseIso) : new Date();
  const next = new Date(baseDate.getTime());
  next.setHours(next.getHours() + safeHours);
  return next.toISOString();
};

const buildPaymentExpiryIsoFromMinutes = (minutes: number, baseIso?: string) => {
  const safeMinutes = Number.isFinite(minutes) ? Math.max(0, minutes) : 0;
  const baseDate = baseIso ? new Date(baseIso) : new Date();
  const next = new Date(baseDate.getTime());
  next.setMinutes(next.getMinutes() + safeMinutes);
  return next.toISOString();
};

const resolveTicketExpiryFromConfig = (ticket: any) => {
  const unit = (ticket?.fixedPaymentExpirationUnit || '').toString().toLowerCase();
  const days = ticket?.fixedPaymentExpirationDays;
  const hours = ticket?.fixedPaymentExpirationHours;
  if (unit === 'minutes' && (hours === 0 || !!hours)) {
    return buildPaymentExpiryIsoFromMinutes(Number(hours));
  }
  if (unit === 'hours' && (hours === 0 || !!hours)) {
    return buildPaymentExpiryIsoFromHours(Number(hours));
  }
  if (unit === 'days' && (days === 0 || !!days)) {
    return buildPaymentExpiryIsoFromDays(Number(days));
  }
  // Backwards compatible fallbacks
  if (days === 0 || !!days) return buildPaymentExpiryIsoFromDays(Number(days));
  if (hours === 0 || !!hours) return buildPaymentExpiryIsoFromHours(Number(hours));
  if (ticket?.fixedPaymentExpirationAt) return ticket.fixedPaymentExpirationAt;
  return undefined;
};

// A ticket expiry config of 0 (minutes/hours/days) or a fixed date in the past would
// approve the booking with a payment link that is already expired (and the scheduler
// would auto-reject it on its next run): such approvals are refused.
export const BORN_EXPIRED_PAYMENT_LINK_MESSAGE =
  "This ticket's payment expiration setting (0 or a fixed date in the past) would create an already expired payment link. Update the ticket's payment expiration (or set an expiry for this booking) before approving.";
const MIN_PAYMENT_WINDOW_MS = 60 * 1000;
const isBornExpiredPaymentExpiry = (expiryIso?: string | null) => {
  if (!expiryIso) return false;
  const ts = new Date(expiryIso).getTime();
  return !Number.isNaN(ts) && ts <= Date.now() + MIN_PAYMENT_WINDOW_MS;
};
/** Ticket-config expiry for an approval; throws when it would be born expired. */
const resolveApprovalExpiryFromConfig = (ticket: any, guestName?: string | null) => {
  const resolved = resolveTicketExpiryFromConfig(ticket);
  if (isBornExpiredPaymentExpiry(resolved)) {
    throw new Error(`${guestName ? `${guestName}: ` : ''}${BORN_EXPIRED_PAYMENT_LINK_MESSAGE}`);
  }
  return resolved;
};

// Group payment rule (same as validateBooking in the payment Lambda): the group can pay
// only once no member is undecided (rejected/cancelled/banned/transfered are decided).
const UNDECIDED_GROUP_STATUSES: string[] = [
  BookingStatus.PENDING,
  BookingStatus.PARTIALLY_APPROVED,
  BookingStatus.NOT_REGISTERED,
];
const isUndecidedStatus = (status?: string | null) =>
  UNDECIDED_GROUP_STATUSES.includes(status || '');

// Fallback main-booking lookup among the main guest's bookings for the event (when
// mainBookingId is missing): never an old group's rejected/cancelled main booking,
// preferring the edited booking's orderId.
export const pickMainGuestBooking = (bookings: any[], orderId?: string | null) => {
  const candidates = (bookings || []).filter(
    (b: any) =>
      b?.isMainGuest &&
      b.status !== BookingStatus.REJECTED &&
      b.status !== BookingStatus.CANCELLED,
  );
  return (
    (orderId && candidates.find((b: any) => b.orderId === orderId)) ||
    candidates[0] ||
    null
  );
};

/**
 * Unambiguous payment deadline for emails/SMS, always in Cairo time,
 * e.g. "21 Oct 2026, 7:30 PM (Cairo time)". 'N/A' when missing/invalid.
 */
export const formatPaymentDeadline = (iso?: string | null) => {
  if (!iso) return 'N/A';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Cairo',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Africa/Cairo',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  return `${day}, ${time} (Cairo time)`;
};

/**
 * overallStatus of a main booking from its own status and its members' statuses
 * (cancelled/transfered members are ignored): main rejected -> rejected (the group is
 * rejected with it), everyone approved -> approved, nobody approved -> pending,
 * otherwise partially approved.
 */
export const computeGroupOverallStatus = (
  mainStatus?: string | null,
  memberStatuses: (string | null | undefined)[] = [],
) => {
  if (mainStatus === BookingStatus.REJECTED) return BookingStatus.REJECTED;
  const considered = [
    mainStatus,
    ...memberStatuses.filter(
      (s) => s !== BookingStatus.CANCELLED && s !== BookingStatus.TRANSFERED,
    ),
  ];
  if (considered.every((s) => s === BookingStatus.APPROVED))
    return BookingStatus.APPROVED;
  if (!considered.some((s) => s === BookingStatus.APPROVED))
    return BookingStatus.PENDING;
  return BookingStatus.PARTIALLY_APPROVED;
};

// A guest may hold only one live booking per event: approving one is refused while
// the same guest has another booking in these statuses (or a paid one).
const ACTIVE_GUEST_BOOKING_STATUSES: string[] = [
  BookingStatus.APPROVED,
  BookingStatus.PENDING,
  BookingStatus.PARTIALLY_APPROVED,
];
export const DUPLICATE_GUEST_BOOKING_MESSAGE =
  'This guest already has another active booking for this event (approved, pending or paid). Reject or cancel that booking before approving this one.';

/** The same guest's OTHER live booking in the event that blocks approving `booking`, or null. */
export const findConflictingGuestBooking = (booking: any, guestEventBookings: any[]) =>
  (guestEventBookings || []).find(
    (b: any) =>
      b?.id &&
      b.id !== booking?.id &&
      b.deleted !== '1' &&
      !b._deleted &&
      (ACTIVE_GUEST_BOOKING_STATUSES.includes(b.status) ||
        (b.isPaid === true &&
          b.status !== BookingStatus.TRANSFERED &&
          b.status !== BookingStatus.CANCELLED)),
  ) || null;

/**
 * Creating a booking is refused while the guest (by id or phone) already holds one of
 * these in the event: the approval set plus an open invitation (not registered) and a
 * ticket being transferred.
 */
const findConflictingBookingForCreate = (guestEventBookings: any[], excludeId?: string) =>
  findConflictingGuestBooking({ id: excludeId }, guestEventBookings) ||
  (guestEventBookings || []).find(
    (b: any) =>
      b?.id &&
      b.id !== excludeId &&
      b.deleted !== '1' &&
      !b._deleted &&
      (b.status === BookingStatus.NOT_REGISTERED ||
        b.status === BookingStatus.TRANSFERED),
  ) ||
  null;

/** Oldest first (createdAt, then id): the booking kept among concurrent duplicates. */
const compareBookingAge = (a: any, b: any) => {
  const ta = new Date(a?.createdAt || 0).getTime() || 0;
  const tb = new Date(b?.createdAt || 0).getTime() || 0;
  if (ta !== tb) return ta - tb;
  return String(a?.id).localeCompare(String(b?.id));
};

const toLowerStatus = (status?: string | null) =>
  typeof status === 'string' ? status.toLowerCase() : status;

// Minimal rows to detect a guest's other bookings in an event (by guest id or phone)
const guestConflictRowsByEventId = /* GraphQL */ `
  query GuestConflictRowsByEventId(
    $bookingEventId: ID!
    $filter: ModelBookingFilterInput
    $limit: Int
    $nextToken: String
  ) {
    bookingByEventId(
      bookingEventId: $bookingEventId
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        isPaid
        deleted
        isMainGuest
        bookingGuestId
        bookingMainGuestId
        phone_number
        createdAt
        _version
        _deleted
      }
      nextToken
    }
  }
`;

const customUpdateBooking = /* GraphQL */ `mutation CustomUpdateBooking(
  $input: UpdateBookingInput!
  $condition: ModelBookingConditionInput
) {
  updateBooking(input: $input, condition: $condition) {
    id
    status
    overallStatus
    bookingGuestId
    bookingMainGuestId
    mainBookingId
    bookingEventId
    bookingEventTicketId
    wave
    waveId
    isMainGuest
    isInvited
    orderId
    ticketNumber
    redeemed
    statusUpdatedByID
    statusUpdatedByName
    statusUpdatedAt
    paymentExpirationAt
    paymentLinkUpdatedAt
    expirationReminderSentAt
    expirationReminderTargetAt
    waveWarningSentAt
    waveWarningTargetStartAt
    specialNeed
    phone_number
    guestName
    notRegisteredGuests
    hasNotRegisteredGuest
    deleted
    createdAt
    createdByID
    createdByName
    isPaid
    isTransfered
    isPendingTransfer
    lastGuestId
    lastMainGuestId
    ownerHistory
    paidAmount
    balance
    scannedAt
    scannedByName
    refunded
    paymentTransactionId
    paymentParentTransactionId
    origin
    paymentMethod
    testField
    updatedAt
    _version
    _deleted
    _lastChangedAt
    bookingRejectionReasonId
    __typename
  }
}
`;

const customCreateBooking = /* GraphQL */ `mutation CustomCreateBooking(
  $input: CreateBookingInput!
  $condition: ModelBookingConditionInput
) {
  createBooking(input: $input, condition: $condition) {
    id
    status
    overallStatus
    bookingGuestId
    bookingMainGuestId
    mainBookingId
    bookingEventId
    bookingEventTicketId
    wave
    waveId
    isMainGuest
    isInvited
    orderId
    ticketNumber
    redeemed
    statusUpdatedByID
    statusUpdatedByName
    statusUpdatedAt
    paymentExpirationAt
    paymentLinkUpdatedAt
    expirationReminderSentAt
    expirationReminderTargetAt
    waveWarningSentAt
    waveWarningTargetStartAt
    specialNeed
    phone_number
    guestName
    notRegisteredGuests
    hasNotRegisteredGuest
    deleted
    createdAt
    createdByID
    createdByName
    isPaid
    isTransfered
    isPendingTransfer
    lastGuestId
    lastMainGuestId
    ownerHistory
    paidAmount
    balance
    scannedAt
    scannedByName
    refunded
    paymentTransactionId
    paymentParentTransactionId
    origin
    paymentMethod
    testField
    updatedAt
    _version
    _deleted
    _lastChangedAt
    bookingRejectionReasonId
    __typename
  }
}
`;

const useBooking = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { showConfirm, showError, showWarning } = useApp();
  const bookingsListing = useSelector(
    (state: any) => state.bookings?.listing ?? [],
  );
  const { guestsGet } = useGuest('guests', 'guest');
  const bookingRegisteredFilter = useSelector(
    (state: any) => state.bookings.registeredFilter,
  );
  const bookingStatusFilter = useSelector(
    (state: any) => state.bookings.statusFilter,
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]['nextToken'],
  );
  const session = useSelector((state: any) => state.app.session);
  const serverAddress = useSelector((state: any) => state.server.serverAddress);

  const sendSmsToUser = async (
    phone: string | null | undefined,
    message: string,
  ) => {
    if (!phone) return;
    try {
      await sendSms(phone, message);
    } catch (err) {
      console.error('Failed to send SMS:', err);
    }
  };

  const sendEmailSafe = async (payload: any) => {
    try {
      await sendEmail(payload);
    } catch (err) {
      console.error(`Failed to send email (${payload?.templateName}):`, err);
    }
  };

  /**
   * {{rejectionMessage}} of the rejection email/SMS: the rejection reason's name and the
   * admin's comment ("Reason - comment"), or whichever exists; "" when neither.
   */
  const buildRejectionMessage = (
    reasonId?: string | null,
    comment?: string | null,
    booking?: any,
  ) => {
    const reasons: any[] =
      (store.getState() as any)?.rejectionReasons?.listing ?? [];
    const reasonText = String(
      (reasonId && reasons.find((r: any) => r?.id === reasonId)?.content) ||
        (reasonId && booking?.rejectionReason?.id === reasonId
          ? booking?.rejectionReason?.content
          : '') ||
        '',
    ).trim();
    const note = String(comment || '').trim();
    if (reasonText && note && reasonText.toLowerCase() !== note.toLowerCase()) {
      return `${reasonText} - ${note}`;
    }
    return reasonText || note || '';
  };

  /**
   * {{amount}} / {{ticketsCount}} of the approval / payment-reminder emails: what the
   * group payment link charges = wave price of the main booking x approved, unpaid
   * bookings of the group (main incl.), the same rule as the payment Lambda.
   * `fresh` overrides listed rows that were just updated (GSI reads can be stale);
   * `extraWaves` are searched too (e.g. the new ticket's waves after a ticket change).
   * Never throws; "" when unknown.
   */
  const buildGroupPaymentSummary = async (
    main: any,
    fresh: any[] = [],
    extraWaves: any[] = [],
  ): Promise<{ amount: string; ticketsCount: string }> => {
    const empty = { amount: '', ticketsCount: '' };
    try {
      if (!main?.id) return empty;
      const freshById = new Map<string, any>(
        (fresh || []).filter((b: any) => b?.id).map((b: any) => [b.id, b]),
      );
      const mainRow = { ...main, ...(freshById.get(main.id) || {}) };
      const listed: any[] = await fetchBookingsByMainBookingId(main.id, {
        deleted: { eq: '0' },
      });
      const members = new Map<string, any>();
      for (const b of listed) {
        if (b?.id && b.id !== main.id) members.set(b.id, b);
      }
      freshById.forEach((b, id) => {
        if (id === main.id || b?.isMainGuest) return;
        if (members.has(id) || b?.mainBookingId === main.id) {
          members.set(id, { ...(members.get(id) || {}), ...b });
        }
      });
      const payable = [mainRow, ...Array.from(members.values())].filter(
        (b: any) =>
          b?.status === BookingStatus.APPROVED &&
          b.isPaid !== true &&
          b.deleted !== '1',
      );
      if (!payable.length) return empty;
      const ticketsCount = String(payable.length);
      const waves: any[] = [
        ...(mainRow.eventTicket?.waves || []),
        ...(extraWaves || []),
      ];
      const wave = waves.find((w: any) => w && w.id === mainRow.waveId);
      const price = Number(wave?.price);
      if (!wave || !Number.isFinite(price) || price <= 0) {
        return { amount: '', ticketsCount };
      }
      return {
        amount: `${(payable.length * price).toLocaleString('en-US')} EGP`,
        ticketsCount,
      };
    } catch (err) {
      console.error('buildGroupPaymentSummary failed:', err);
      return empty;
    }
  };

  /** The guest's "booking rejected" email/SMS (same text for a single reject and a group cascade). */
  const sendBookingRejectedNotification = async (params: {
    guest: any;
    eventName?: string | null;
    rejectionMessage?: string | null;
    email: boolean;
    sms: boolean;
  }) => {
    const { guest, eventName, rejectionMessage, email, sms } = params;
    if (guest?.email && email) {
      try {
        await sendEmail({
          customerEmail: guest?.email,
          eventName,
          guestName: guest?.name,
          rejectionMessage,
          templateName: 'UlterRejectedReserved',
        });
      } catch (emailErr) {
        console.error('Failed to send the rejection email:', emailErr);
      }
    }
    if (guest?.phone_number && sms) {
      try {
        let textContent = `Hi ${guest.name}, We regret to inform you that your booking request for the "${eventName}" event has been rejected.`;
        if (rejectionMessage) {
          textContent += ` Reason for Rejection: ${rejectionMessage}`;
        }
        await sendSmsToUser(guest.phone_number, textContent);
      } catch (smsErr) {
        console.error('Failed to send the rejection SMS:', smsErr);
      }
    }
  };

  const buildPaymentLink = (
    bookingId: string,
    origin: PaymentLinkOrigin,
    _expiresAt?: string | null,
    _linkUpdatedAt?: string | null,
  ) => {
    return buildPaymentPageUrl(bookingId, origin, _linkUpdatedAt ?? undefined, _expiresAt);
  };

  /** Recompute and persist payment window on the main guest booking (group link); resets reminder state for a new clock. */
  const resetMainGuestPaymentWindow = async (main: any) => {
    if (!main?.id || main._version == null) {
      return { paymentExpirationAt: buildPaymentExpiryIso(), mainUpdated: null as any };
    }
    let newExp: string;
    if (!main.bookingEventTicketId) {
      newExp = buildPaymentExpiryIso();
    } else {
      const eventTicketRes: any = await client.graphql({
        query: getEventTicket,
        variables: { id: main.bookingEventTicketId },
        authMode: 'userPool',
      });
      const ticket = eventTicketRes?.data?.getEventTicket;
      newExp =
        resolveApprovalExpiryFromConfig({
          fixedPaymentExpirationUnit: ticket?.fixedPaymentExpirationUnit,
          fixedPaymentExpirationDays:
            ticket?.fixedPaymentExpirationDays === 0 || ticket?.fixedPaymentExpirationDays
              ? Number(ticket?.fixedPaymentExpirationDays)
              : undefined,
          fixedPaymentExpirationHours:
            ticket?.fixedPaymentExpirationHours === 0 || ticket?.fixedPaymentExpirationHours
              ? Number(ticket?.fixedPaymentExpirationHours)
              : undefined,
          fixedPaymentExpirationAt: ticket?.fixedPaymentExpirationAt,
        }, main.guest?.name || main.guestName) || buildPaymentExpiryIso();
      // DB02: Auto Rejection on -> the window can't outlast the main booking's wave
      const mainWaveEndTs = main.isPaid === true
        ? null
        : getAutoRejectWaveEndTs({
          published: main.event?.published,
          autoRejectionEnabled: main.event?.autoRejectionEnabled,
          waves: ticket?.waves,
          waveId: main.waveId,
        });
      if (mainWaveEndTs !== null && mainWaveEndTs > Date.now()) {
        newExp = capExpiryToWaveEnd(newExp, mainWaveEndTs) || newExp;
      }
    }
    const now = new Date().toISOString();
    const writeWindow = (version: number) =>
      client.graphql({
        query: customUpdateBooking,
        variables: {
          input: {
            id: main.id,
            _version: version,
            paymentExpirationAt: newExp,
            paymentLinkUpdatedAt: now,
            expirationReminderSentAt: null,
            expirationReminderTargetAt: null,
          },
        },
        authMode: 'userPool',
      });
    let res: any;
    try {
      res = await writeWindow(main._version);
    } catch (firstErr) {
      // Stale _version (the main row was read with an eventually consistent get, or
      // changed concurrently): re-read once and retry, so the link we send matches the DB.
      const fresh: any = await client.graphql({
        query: getBooking,
        variables: { id: main.id },
        authMode: 'userPool',
      });
      const freshVersion = fresh?.data?.getBooking?._version;
      if (freshVersion == null || freshVersion === main._version) throw firstErr;
      res = await writeWindow(freshVersion);
    }
    return { paymentExpirationAt: newExp, mainUpdated: res.data?.updateBooking };
  };

  /**
   * Give every approved, unpaid companion the main booking's payment window. The payment
   * Lambda checks each selected member's own expiry, so a stale companion expiry would
   * block the group payment (and the scheduler would auto-reject that member).
   * `fresh` overrides list rows that were just updated (GSI reads can be stale).
   * Returns the number of members that could not be updated.
   */
  const alignGroupPaymentWindow = async (
    mainBookingId: string,
    paymentExpirationAt: string,
    fresh: any[] = [],
  ) => {
    const now = new Date().toISOString();
    const listed: any[] = await fetchBookingsByMainBookingId(mainBookingId, {
      deleted: { eq: '0' },
    });
    const byId = new Map<string, any>(listed.map((b) => [b.id, b]));
    for (const b of fresh) {
      if (b?.id && !b.isMainGuest) byId.set(b.id, b);
    }
    const toUpdate = Array.from(byId.values()).filter(
      (b) => b.status === BookingStatus.APPROVED && !b.isPaid,
    );
    const results = await Promise.allSettled(
      toUpdate.map((b) =>
        client.graphql({
          query: customUpdateBooking,
          variables: {
            input: {
              id: b.id,
              _version: b._version,
              paymentExpirationAt,
              paymentLinkUpdatedAt: now,
              expirationReminderSentAt: null,
              expirationReminderTargetAt: null,
            },
          },
          authMode: 'userPool',
        }),
      ),
    );
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      console.error('alignGroupPaymentWindow: some members failed', failed);
    }
    return failed.length;
  };

  const createWorkflowTimeline = async (params: {
    actionName: string;
    oldStatus: string;
    newStatus: string;
    bookingId?: string;
    customerId?: string;
    description?: string;
    userID: string;
    userName: string;
  }) => {
    try {
      await client.graphql({
        query: createTimeline,
        variables: {
          input: {
            actionName: params.actionName,
            oldStatus: params.oldStatus,
            newStatus: params.newStatus,
            bookingId: params.bookingId,
            customerId: params.customerId,
            description: params.description || '',
            type: 'info',
            isScanned: '0',
            deleted: '0',
            createdAt: new Date().toISOString(),
            createdByID: params.userID,
            createdByName: params.userName,
          },
        },
        authMode: 'userPool',
      });
    } catch (err) {
      console.error(`Failed to create timeline "${params.actionName}":`, err);
    }
  };

  /** Merge freshly updated booking fields into the rows currently shown in the listing. */
  const patchListingRows = (updatedRows: any[]) => {
    const rows = (updatedRows || []).filter((r) => r?.id);
    if (!rows.length) return;
    // Cached filter results (shown instantly when the list is reopened) too
    patchListResultRows(rows);
    const currentListing: any[] =
      (store.getState() as any)?.bookings?.listing ?? [];
    if (!currentListing.length) return;
    const byId = new Map<string, any>(rows.map((r) => [r.id, r]));
    if (!currentListing.some((row: any) => byId.has(row?.id))) return;
    dispatch(
      setListing(
        currentListing.map((row: any) => {
          const u = byId.get(row?.id);
          if (!u) return row;
          return {
            ...row,
            status: u.status,
            overallStatus: u.overallStatus,
            statusUpdatedByID: u.statusUpdatedByID,
            statusUpdatedByName: u.statusUpdatedByName,
            statusUpdatedAt: u.statusUpdatedAt,
            bookingRejectionReasonId: u.bookingRejectionReasonId,
            _version: u._version,
          };
        }),
      ),
    );
  };

  /**
   * Group members of a main booking: live bookings whose mainBookingId is the main id.
   * Legacy rows without mainBookingId: same orderId, else the main guest's bookings for
   * the event. Never another group's main booking or a row linked to another main.
   */
  const fetchGroupMembersForMain = async (main: any) => {
    const mainId = main?.id;
    if (!mainId) return [] as any[];
    const isLive = (b: any) => b && b.deleted === '0' && !b._deleted;
    let members: any[] = (
      await fetchBookingsByMainBookingId(mainId, { deleted: { eq: '0' } })
    ).filter((b: any) => isLive(b) && b.id !== mainId && !b.isMainGuest);
    const mainGuestId = main.bookingMainGuestId || main.bookingGuestId;
    if (!members.length && mainGuestId && main.bookingEventId) {
      const guestRows: any[] = await fetchBookingsByMainGuestId(mainGuestId, {
        bookingEventId: { eq: main.bookingEventId },
        deleted: { eq: '0' },
      });
      const legacy = (guestRows || []).filter(
        (b: any) =>
          isLive(b) &&
          b.id !== mainId &&
          !b.isMainGuest &&
          (!b.mainBookingId || b.mainBookingId === '0' || b.mainBookingId === mainId),
      );
      const sameOrder = main.orderId
        ? legacy.filter((b: any) => b.orderId === main.orderId)
        : [];
      members = sameOrder.length
        ? sameOrder
        : legacy.filter(
          (b: any) => !main.orderId || !b.orderId || b.orderId === main.orderId,
        );
    }
    return members;
  };

  /**
   * Rejecting a main booking rejects its group: every pending / partially approved /
   * not registered / approved-unpaid member is rejected too. Paid members are left as
   * they are (warning to the admin); rejected/cancelled/other members are skipped.
   * Each member gets one rejection email/SMS when the event's switches allow it.
   */
  const cascadeRejectGroupMembers = async (
    mainBooking: any,
    opts: {
      userID: string;
      userName: string;
      rejectionReasonId?: string | null;
      rejectionComment?: string | null;
      event?: any;
      notify?: boolean;
    },
  ) => {
    const result = {
      rejected: [] as any[],
      paidSkipped: [] as any[],
      failed: [] as any[],
    };
    if (!mainBooking?.id) return result;
    const { userID, userName, rejectionReasonId, rejectionComment } = opts;
    const event = opts.event || mainBooking.event;
    const members = await fetchGroupMembersForMain(mainBooking);
    const cascadeStatuses: string[] = [
      BookingStatus.PENDING,
      BookingStatus.PARTIALLY_APPROVED,
      BookingStatus.NOT_REGISTERED,
      BookingStatus.APPROVED,
    ];
    const toReject: any[] = [];
    for (const m of members) {
      if (!cascadeStatuses.includes(m.status)) continue;
      if (m.isPaid === true) {
        result.paidSkipped.push(m);
        continue;
      }
      toReject.push(m);
    }

    const shouldSendEmail = event?.sendEmailToValidGuest ?? true;
    const shouldSendSms = event?.sendSmsToValidGuest ?? true;
    const mainName = mainBooking.guest?.name || mainBooking.guestName || '';

    await Promise.all(
      toReject.map(async (m) => {
        const now = new Date().toISOString();
        const input: any = {
          id: m.id,
          status: BookingStatus.REJECTED,
          overallStatus: BookingStatus.REJECTED,
          statusUpdatedByID: userID,
          statusUpdatedByName: userName,
          statusUpdatedAt: now,
          _version: m._version,
        };
        if (rejectionReasonId) input.bookingRejectionReasonId = rejectionReasonId;
        if (rejectionComment) input.rejectionComment = rejectionComment;
        try {
          const res: any = await client.graphql({
            query: customUpdateBooking,
            variables: {
              input,
              // Never reject a member that got paid in the meantime
              condition: {
                or: [
                  { isPaid: { attributeExists: false } },
                  { isPaid: { ne: true } },
                ],
              },
            },
            authMode: 'userPool',
          });
          const updatedMember = res?.data?.updateBooking || { ...m, ...input };
          result.rejected.push(updatedMember);
          await createWorkflowTimeline({
            actionName: 'Rejected with main guest',
            oldStatus: m.status,
            newStatus: 'Rejected',
            bookingId: m.id,
            customerId: m.bookingGuestId || m.guest?.id || m.bookingMainGuestId,
            description: `Rejected with main guest${mainName ? ` (${mainName})` : ''} by ${userName}`,
            userID,
            userName,
          });
          if (opts.notify !== false && (shouldSendEmail || shouldSendSms)) {
            await sendBookingRejectedNotification({
              guest: m.guest,
              eventName: event?.name,
              rejectionMessage: buildRejectionMessage(
                rejectionReasonId || m.bookingRejectionReasonId,
                rejectionComment || m.rejectionComment,
                m,
              ),
              email: shouldSendEmail,
              sms: shouldSendSms,
            });
          }
        } catch (err) {
          console.error('cascadeRejectGroupMembers: member not rejected', m.id, err);
          result.failed.push(m);
        }
      }),
    );

    patchListingRows(result.rejected);

    const memberName = (b: any) => b?.guest?.name || b?.guestName || 'A group member';
    const warnings: string[] = result.paidSkipped.map(
      (b) => `${memberName(b)} already paid — not rejected. Refund/handle manually.`,
    );
    if (result.failed.length) {
      warnings.push(
        `Could not reject ${result.failed.map(memberName).join(', ')} with the main guest (changed meanwhile or paid). Please check them manually.`,
      );
    }
    if (warnings.length) showWarning(warnings.join(' '));
    return result;
  };

  async function fetch(params: BookingListingVariables) {
    const {
      searchText,
      generalFilters,
      isMainGuest,
      status,
      overallStatus,
      mainGuestID,
      guestID,
      eventID,
    } = params;
    try {
      // Same filter as the exports (buildBookingListFilter); the caller's
      // generalFilters are not modified.
      const { filter, createdAtFilter } = buildBookingListFilter({
        generalFilters,
        searchText,
        statusFilter: bookingStatusFilter,
        isMainGuest,
        status,
        overallStatus,
        mainGuestID,
        guestID,
        eventID,
      });
      if (!generalFilters?.event) return [];
      // freshStart: new listing from page 1 (background refresh of a cached
      // filter result) - ignore the accumulated rows / token in Redux.
      const pageToken = params.freshStart ? null : nextToken;
      let variables: any = {
        filter,
        limit: pageToken ? BOOKINGS_NEXT_PAGE_LIMIT : BOOKINGS_FIRST_PAGE_LIMIT,
        nextToken: pageToken,
        bookingEventId: generalFilters?.event,
        sortDirection: ModelSortDirection.DESC,
      };

      if (createdAtFilter) {
        variables.createdAt = createdAtFilter;
      }
      // Single `status` value selected (Rejected / Transfered / Cancelled tabs,
      // or an explicit `status` param): read only that status' partition of
      // the event via the bookingsByEventAndStatus GSI instead of scanning the
      // whole event and filtering. The sort key is now `status`, so the date
      // range moves into the filter. `overallStatus` tabs (Pending, Approved,
      // Partially Approved, All) keep the createdAt index path.
      // The choice is deterministic per filter set (a filter change clears
      // nextToken), so every page of one listing uses the same index.
      const indexStatus: string | undefined =
        USE_STATUS_INDEX &&
        statusIndexAvailable &&
        typeof filter.status?.eq === 'string' &&
        filter.status.eq !== ''
          ? filter.status.eq
          : undefined;

      let currentNextToken: string | null | undefined;
      let responseListing: any[] | undefined;
      let servedByStatusIndex = false;
      if (indexStatus) {
        // status is the key condition now, not a filter
        const indexFilter: ModelBookingFilterInput = { ...filter };
        delete indexFilter.status;
        if (createdAtFilter) indexFilter.createdAt = createdAtFilter;
        try {
          const statusList: any = await client.graphql({
            query: bookingsByEventAndStatusListing,
            variables: {
              filter: indexFilter,
              limit: variables.limit,
              nextToken: variables.nextToken,
              bookingEventId: variables.bookingEventId,
              status: { eq: indexStatus },
              sortDirection: ModelSortDirection.DESC,
            },
            authMode: 'userPool',
          });
          currentNextToken = statusList.data.bookingsByEventAndStatus.nextToken;
          responseListing = statusList.data.bookingsByEventAndStatus.items;
          servedByStatusIndex = true;
        } catch (err) {
          // Only fall back when the API doesn't know the new query yet
          // (frontend deployed before `amplify push`). Mid-pagination the
          // nextToken belongs to the status index, so it can't be reused.
          if (!isUnknownStatusIndexError(err) || variables.nextToken) throw err;
          statusIndexAvailable = false;
          console.warn(
            'bookingsByEventAndStatus is not deployed yet; falling back to bookingByEventId.',
            err,
          );
        }
      }
      if (!servedByStatusIndex) {
        // Lean selection set: list rows only (details/actions re-fetch the full booking).
        const bookingList: any = await client.graphql({
          query: bookingByEventIdListing,
          variables,
          authMode: 'userPool',
        });
        currentNextToken = bookingList.data.bookingByEventId.nextToken;
        responseListing = bookingList.data.bookingByEventId.items;
      }
      // Filters changed while this page was in flight: drop the response so it
      // can't be appended to (or overwrite) the listing of the newer filters.
      if (params.isStale?.()) return;

      let listing = params.freshStart
        ? [...(responseListing ?? [])]
        : [...(bookingsListing ?? []), ...(responseListing ?? [])];
      dispatch(setListing(listing));
      dispatch(setNextToken(currentNextToken));
      return listing;
    } catch (err) {
      showError(err);
    }
  }
  // Function to remove cancelled bookings if there are non-cancelled bookings
  const removeCancelledIfNonCancelledExists = (bookings: Booking[]) => {
    const groupedBookings = bookings.reduce(
      (acc, booking) => {
        if (!acc[booking.bookingGuestId ?? '']) {
          acc[booking.bookingGuestId ?? ''] = [];
        }
        acc[booking.bookingGuestId ?? ''].push(booking);
        return acc;
      },
      {} as Record<string, Booking[]>,
    );

    return Object.values(groupedBookings).flatMap((guestBookings) => {
      const nonCancelledBookings = guestBookings.filter(
        (b) => b.deleted === '0',
      );
      if (nonCancelledBookings.length > 0) {
        // Remove all cancelled bookings if there are non-cancelled bookings
        return guestBookings.filter((b) => b.deleted === '0');
      } else {
        // Remove duplicate cancelled bookings, keep only one
        const cancelledBookings = guestBookings.filter(
          (b) => b.deleted === '1',
        );
        if (cancelledBookings.length > 1) {
          return [cancelledBookings[0]]; // Keep only one cancelled booking
        } else {
          return cancelledBookings;
        }
      }
    });
  };

  const fetchAllBookingsByMainGuestId = async (filter: any) => {
    try {
      const { eventID, mainGuestID, deleted } = filter;
      let queryFilter: ModelBookingFilterInput = {
        bookingEventId: { eq: eventID },
        deleted: { eq: deleted },
      };
      const bookingList: any = await fetchBookingsByMainGuestId(
        mainGuestID,
        queryFilter,
      );
      const accompaniedGuestsBooking = bookingList.filter(
        (booking: Booking) => !booking.isMainGuest,
      );
      const mainGuestBooking = bookingList.filter(
        (booking: Booking) => booking.isMainGuest,
      );

      const cleanedAccompaniedGuestsBooking =
        removeCancelledIfNonCancelledExists(accompaniedGuestsBooking);
      const cleanedMainGuestBooking =
        removeCancelledIfNonCancelledExists(mainGuestBooking);

      return {
        accomaniedGuests: cleanedAccompaniedGuestsBooking,
        mainGuestBooking: cleanedMainGuestBooking[0] || null,
      };
    } catch (error) {
      showError(error);
    }
  };
  const fetchAllBookingsByEventId = async (filter: any) => {
    try {
      const { eventID } = filter;
      const bookingList: any = await client.graphql({
        query: bookingByEventId,
        variables: { filter: {}, bookingEventId: eventID, limit: 100000 },
        authMode: 'userPool',
      });
      const responseListing = bookingList.data.bookingByEventId.items;
      return responseListing;
    } catch (error) {
      showError(error);
    }
  };
  const fetchBookingsList = async (
    filter: any,
    nextToken: string | undefined,
  ) => {
    try {
      const variables: any = { filter };
      if (nextToken) {
        variables.nextToken = nextToken;
      }
      variables.limit = 100000;
      const response: any = await client.graphql<Booking>({
        query: listBookings,
        variables: variables,
        authMode: 'userPool',
      });
      const bookings = response.data.listBookings;
      return bookings;
    } catch (err) {
      console.error('Error fetching bookings list:', err);
      throw err;
    }
  };

  const fetchBookings = async (filter: any) => {
    try {
      let userBookingsComplete: Booking[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const userBookingsListNew = await fetchBookingsList(filter, nextToken);
        userBookingsComplete.push(...userBookingsListNew.items);
        nextToken = userBookingsListNew.nextToken;
      } while (nextToken);

      return userBookingsComplete;
    } catch (err) {
      console.error('Error fetching user bookings:', err);
      throw err;
    }
  };

  const fetchBookingsListByGuestId = async (
    filter: any,
    bookingGuestId: string,
    nextToken: string | undefined,
  ) => {
    try {
      const variables: any = { bookingGuestId, filter };
      if (filter) {
        variables.filter = filter;
      }
      if (nextToken) {
        variables.nextToken = nextToken;
      }
      variables.limit = 100000;
      const response: any = await client.graphql<Booking>({
        query: bookingByGuestId,
        variables: variables,
        authMode: 'userPool',
      });
      const bookings = response.data.bookingByGuestId;
      return bookings;
    } catch (err) {
      console.error('Error fetching bookings list:', err);
      throw err;
    }
  };

  const fetchBookingsByGuestId = async (
    bookingGuestId: string,
    filter: any,
  ) => {
    try {
      let userBookingsComplete: Booking[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const userBookingsListNew = await fetchBookingsListByGuestId(
          filter,
          bookingGuestId,
          nextToken,
        );
        userBookingsComplete.push(...userBookingsListNew.items);
        nextToken = userBookingsListNew.nextToken;
      } while (nextToken);

      return userBookingsComplete;
    } catch (err) {
      console.error('Error fetching user bookings:', err);
      throw err;
    }
  };

  /**
   * Live (not deleted) bookings of the event that belong to the guest id OR to any
   * stored format of the phone number (phone-only / not registered bookings have no
   * guest id). Throws on a failed lookup: callers refuse the action then.
   */
  const fetchGuestEventBookingsForConflict = async (
    bookingEventId?: string | null,
    bookingGuestId?: string | null,
    phones: (string | null | undefined)[] = [],
  ) => {
    if (!bookingEventId) return [] as any[];
    const rows = new Map<string, any>();
    if (bookingGuestId) {
      const byGuest = await fetchBookingsByGuestId(bookingGuestId, {
        bookingEventId: { eq: bookingEventId },
        deleted: { eq: '0' },
      });
      (byGuest || []).forEach((b: any) => b?.id && rows.set(b.id, b));
    }
    const variants = Array.from(
      new Set(
        phones
          .filter((p): p is string => !!p && !!String(p).trim())
          .flatMap((p) => phoneVariants(p)),
      ),
    );
    if (variants.length) {
      let nextToken: string | undefined;
      do {
        const res: any = await client.graphql({
          query: guestConflictRowsByEventId,
          variables: {
            bookingEventId,
            filter: {
              deleted: { eq: '0' },
              or: variants.map((v) => ({ phone_number: { eq: v } })),
            },
            limit: 1000,
            ...(nextToken ? { nextToken } : {}),
          },
          authMode: 'userPool',
        });
        const page = res?.data?.bookingByEventId;
        (page?.items || []).forEach((b: any) => {
          if (b?.id && !rows.has(b.id)) rows.set(b.id, b);
        });
        nextToken = page?.nextToken || undefined;
      } while (nextToken);
    }
    return Array.from(rows.values()).filter((b: any) => !b._deleted);
  };

  const fetchBookingsListByMainGuestId = async (
    filter: any,
    bookingMainGuestId: string,
    nextToken: string | undefined,
  ) => {
    try {
      const variables: any = { bookingMainGuestId };
      if (filter) {
        variables.filter = filter;
      }
      if (nextToken) {
        variables.nextToken = nextToken;
      }
      variables.limit = 100000;
      const response: any = await client.graphql<Booking>({
        query: bookingByMainGuestId,
        variables: variables,
        authMode: 'userPool',
      });
      const bookings = response.data.bookingByMainGuestId;
      return bookings;
    } catch (err) {
      console.error('Error fetching bookings list:', err);
      throw err;
    }
  };

  const fetchBookingsListByMainBookingId = async (
    filter: any,
    mainBookingId: string,
    nextToken: string | undefined,
  ) => {
    try {
      const variables: any = { mainBookingId };
      if (filter) {
        variables.filter = filter;
      }
      if (nextToken) {
        variables.nextToken = nextToken;
      }
      variables.limit = 100000;
      const response: any = await client.graphql<Booking>({
        query: bookingByMainBookingId,
        variables: variables,
        authMode: 'userPool',
      });
      const bookings = response.data.bookingByMainBookingId;
      return bookings;
    } catch (err) {
      console.error('Error fetching bookings list:', err);
      throw err;
    }
  };

  const fetchBookingsByMainGuestId = async (
    bookingMainGuestId: string,
    filter: any,
  ) => {
    try {
      let userBookingsComplete: Booking[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const userBookingsListNew = await fetchBookingsListByMainGuestId(
          filter,
          bookingMainGuestId,
          nextToken,
        );
        userBookingsComplete.push(...userBookingsListNew.items);
        nextToken = userBookingsListNew.nextToken;
      } while (nextToken);

      return userBookingsComplete;
    } catch (err) {
      console.error('Error fetching user bookings:', err);
      throw err;
    }
  };

  const fetchBookingsByMainBookingId = async (
    mainBookingId: string,
    filter: any,
  ) => {
    try {
      let userBookingsComplete: Booking[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const userBookingsListNew = await fetchBookingsListByMainBookingId(
          filter,
          mainBookingId,
          nextToken,
        );
        userBookingsComplete.push(...userBookingsListNew.items);
        nextToken = userBookingsListNew.nextToken;
      } while (nextToken);

      const accompaniedGuestsBooking = userBookingsComplete.filter(
        (booking: Booking) => !booking.isMainGuest,
      );
      return accompaniedGuestsBooking;
    } catch (err) {
      console.error('Error fetching user bookings:', err);
      throw err;
    }
  };

  const fetchBookingsListByEventId = async (
    filter: any,
    bookingEventId: string,
    nextToken: string | undefined,
  ) => {
    try {
      const variables: any = { bookingEventId };
      if (filter) {
        variables.filter = filter;
      }
      if (nextToken) {
        variables.nextToken = nextToken;
      }
      variables.limit = 100000;
      const response: any = await client.graphql<Booking>({
        query: bookingByEventId,
        variables: variables,
        authMode: 'userPool',
      });
      const bookings = response.data.bookingByEventId;
      return bookings;
    } catch (err) {
      console.error('Error fetching bookings list:', err);
      throw err;
    }
  };

  const fetchBookingsByEventId = async (
    bookingEventId: string,
    filter: any,
  ) => {
    try {
      let userBookingsComplete: Booking[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const userBookingsListNew = await fetchBookingsListByEventId(
          filter,
          bookingEventId,
          nextToken,
        );
        userBookingsComplete.push(...userBookingsListNew.items);
        nextToken = userBookingsListNew.nextToken;
      } while (nextToken);

      return userBookingsComplete;
    } catch (err) {
      console.error('Error fetching user bookings:', err);
      throw err;
    }
  };

  /**
   * All bookings of an event matching `filter`, with the lean field set of
   * bookingByEventIdPreview (status, isPaid, phone, guest name / email /
   * phone / gender / avatar / verified) instead of every nested object.
   */
  const fetchBookingsByEventIdLean = async (
    bookingEventId: string,
    filter?: any,
  ): Promise<any[]> => {
    const rows: any[] = [];
    let nextToken: string | null | undefined = undefined;
    do {
      const variables: any = { bookingEventId, limit: 1000, nextToken };
      if (filter) variables.filter = filter;
      const res: any = await client.graphql({
        query: bookingByEventIdPreview,
        variables,
        authMode: 'userPool',
      });
      const page = res?.data?.bookingByEventId;
      rows.push(...(page?.items ?? []).filter(Boolean));
      nextToken = page?.nextToken;
    } while (nextToken);
    return rows;
  };

  /**
   * Existence check ("can this ticket be deleted?"): true as soon as one
   * booking of the event matches `filter`. Ids only, and it stops at the
   * first page that has a match instead of loading every full booking.
   * (Pages evaluate 1000 items: with `limit: 1` an event whose bookings are
   * all of other tickets would take one request per booking.)
   */
  const hasBookingsByEventId = async (
    bookingEventId: string,
    filter: any,
  ): Promise<boolean> => {
    let nextToken: string | null | undefined = undefined;
    do {
      const res: any = await client.graphql({
        query: bookingIdsByEventId,
        variables: { bookingEventId, filter, limit: 1000, nextToken },
        authMode: 'userPool',
      });
      const page = res?.data?.bookingByEventId;
      if ((page?.items ?? []).some(Boolean)) return true;
      nextToken = page?.nextToken;
    } while (nextToken);
    return false;
  };

  /**
   * Ids of the event's active bookings, newest first (same default order as
   * the Bookings list). Ids only - used by booking details "Auto-load next"
   * when the user didn't come from the list.
   */
  const fetchBookingIdsByEventId = async (
    bookingEventId: string,
  ): Promise<string[]> => {
    const ids: string[] = [];
    let nextToken: string | null | undefined = undefined;
    do {
      const response: any = await client.graphql({
        query: bookingIdsByEventId,
        variables: {
          bookingEventId,
          sortDirection: ModelSortDirection.DESC,
          filter: {
            deleted: { eq: '0' },
            overallStatus: { ne: BookingStatus.TEST },
            isPendingTransfer: { ne: true },
          },
          limit: 10000,
          nextToken,
        },
        authMode: 'userPool',
      });
      const page = response?.data?.bookingByEventId;
      (page?.items ?? []).forEach((item: any) => {
        if (item?.id) ids.push(item.id);
      });
      nextToken = page?.nextToken;
    } while (nextToken);
    return ids;
  };

  const getAllBookingsByEventTicketId = async (eventTicketId: string) => {
    try {
      const filter = {
        bookingEventTicketId: { eq: eventTicketId },
        deleted: { eq: '0' },
      };
      return await fetchBookings(filter);
    } catch (err) {
      console.error('Error fetching Event ticket bookings:', err);
      throw err;
    }
  };

  const sortUserUpcomingEventsBookings = (
    userUpcomingEventsBookings: Booking[],
  ): Booking[] | [] => {
    if (userUpcomingEventsBookings.length === 0) {
      return []; // Return an empty array if there are no bookings
    }

    // Sort bookings by event start date
    const sortedBookings = userUpcomingEventsBookings.slice().sort((a, b) => {
      const startDateA = new Date(a.event.startDate!).getTime(); // Get time in milliseconds
      const startDateB = new Date(b.event.startDate!).getTime(); // Get time in milliseconds

      if (startDateA < startDateB) {
        return -1;
      }
      if (startDateA > startDateB) {
        return 1;
      }
      return 0;
    });

    return sortedBookings;
  };

  async function get(params: BookingGetVariables) {
    const { id, listing } = params;
    try {
      let single: Booking | undefined;
      if (listing && listing.length !== 0) {
        single = listing.find((resource: any) => resource.id === id);
      }

      if (single === undefined) {
        const listing: any = await client.graphql({
          query: getBooking,
          variables: { id },
          authMode: 'userPool',
        });
        single = listing.data.getBooking;
      }

      return single;
    } catch (err) {
      showError(err);
    }
  }

  // Booking.bookingGuestSub (owner of the booking's live updates on the website) is kept
  // by the server (op 52, written straight to DynamoDB, works with the current and the
  // staged schema). Best effort, never awaited by the caller: a failure (e.g. this build
  // running before the Lambda with op 52 is deployed) is only logged; the backfill script
  // repairs any gap.
  function syncBookingOwnersInBackground(bookingIds: (string | null | undefined)[]) {
    const ids = Array.from(
      new Set(bookingIds.filter((bookingId): bookingId is string => !!bookingId)),
    ).slice(0, 100);
    if (ids.length === 0) return;
    invokeLambda(LambdaFunctions.ticketingEventsOperations, {
      operationId: 52, // syncBookingOwners
      params: { bookingIds: ids },
    }).catch((syncErr: any) => {
      console.warn('Booking owner sync failed (best effort):', syncErr?.message || syncErr);
    });
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;

    try {
      // One live booking per guest and event (same rule as approvals and the website),
      // matched by guest id and by every format of the phone number.
      const createStatus = toLowerStatus(data.status) || '';
      const isLiveCreate =
        data.deleted !== '1' &&
        createStatus !== BookingStatus.REJECTED &&
        createStatus !== BookingStatus.CANCELLED &&
        createStatus !== BookingStatus.TEST;
      if (isLiveCreate && data.bookingEventId && (data.bookingGuestId || data.phone_number)) {
        const existing = await fetchGuestEventBookingsForConflict(
          data.bookingEventId,
          data.bookingGuestId,
          [data.phone_number],
        );
        if (findConflictingBookingForCreate(existing)) {
          throw new Error(
            `${data.guestName ? `${data.guestName}: ` : ''}${DUPLICATE_GUEST_BOOKING_MESSAGE}`,
          );
        }
      }

      let fixedTicketExpirationAt: string | undefined;
      let fixedTicketExpirationDays: number | undefined;
      let fixedTicketExpirationHours: number | undefined;
      let fixedTicketExpirationUnit: string | undefined;
      if (data.bookingEventTicketId) {
        const eventTicketRes: any = await client.graphql({
          query: getEventTicket,
          variables: { id: data.bookingEventTicketId },
          authMode: 'userPool',
        });
        const ticket = eventTicketRes?.data?.getEventTicket;
        fixedTicketExpirationAt = ticket?.fixedPaymentExpirationAt || undefined;
        const rawDays = ticket?.fixedPaymentExpirationDays;
        fixedTicketExpirationDays = rawDays === 0 || rawDays ? Number(rawDays) : undefined;
        const rawHours = ticket?.fixedPaymentExpirationHours;
        fixedTicketExpirationHours =
          rawHours === 0 || rawHours ? Number(rawHours) : undefined;
        fixedTicketExpirationUnit = ticket?.fixedPaymentExpirationUnit || undefined;
      }
      const createInput: CreateBookingInput = {
        // Booking Status
        status: createStatus,
        overallStatus: toLowerStatus(data.overallStatus) || '',

        // Guest Information
        isMainGuest: data.isMainGuest ? data.isMainGuest : '',
        bookingMainGuestId: data.bookingMainGuestId,
        bookingGuestId: data.bookingGuestId,
        phone_number: data.phone_number ? data.phone_number : '',
        guestName: data.guestName ? data.guestName : '',

        // Booking Information
        specialNeed: data.specialNeed ? data.specialNeed : '',
        redeemed: data.redeemed,
        isPaid: data.isPaid,
        hasNotRegisteredGuest: data.hasNotRegisteredGuest,
        notRegisteredGuests: data.notRegisteredGuests
          ? data.notRegisteredGuests
          : [],

        bookingEventId: data.bookingEventId ? data.bookingEventId : '',
        bookingEventTicketId: data.bookingEventTicketId
          ? data.bookingEventTicketId
          : '',
        wave: data.wave ? data.wave : '',
        waveId: data.waveId ? data.waveId : '',

        // Ticket Details
        orderId: data.orderId ? data.orderId : '',
        cashlessCredit: data.cashlessCredit ? data.cashlessCredit : 0,
        guestTicket: data.guestTicket ? data.guestTicket : null,
        ticketNumber: data.ticketNumber ? data.ticketNumber : '',
        paymentExpirationAt:
          createStatus === BookingStatus.APPROVED
            ? (data as any).paymentExpirationAt ||
            resolveApprovalExpiryFromConfig({
              fixedPaymentExpirationUnit: fixedTicketExpirationUnit,
              fixedPaymentExpirationDays: fixedTicketExpirationDays,
              fixedPaymentExpirationHours: fixedTicketExpirationHours,
              fixedPaymentExpirationAt: fixedTicketExpirationAt,
            }, data.guestName) ||
            undefined
            : undefined,

        // Static Fields
        deleted: '0',
        createdAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
      };

      const res: any = await client.graphql({
        query: customCreateBooking,
        variables: { input: createInput },
        authMode: 'userPool',
      });
      const createdBooking = res.data.createBooking;

      // Concurrent creates (double click, website booking at the same moment) can both
      // pass the check above: re-read and keep only the oldest; this newer one is undone.
      if (isLiveCreate && createdBooking?.id && data.bookingEventId) {
        let concurrent: any[] = [];
        try {
          await new Promise((resolve) => setTimeout(resolve, 400));
          concurrent = (
            await fetchGuestEventBookingsForConflict(
              data.bookingEventId,
              createdBooking.bookingGuestId || data.bookingGuestId,
              [createdBooking.phone_number || data.phone_number],
            )
          ).filter(
            (b: any) =>
              b.id !== createdBooking.id &&
              findConflictingBookingForCreate([b], createdBooking.id),
          );
        } catch (recheckErr) {
          console.error('Duplicate re-check after create failed:', recheckErr);
        }
        const keeper = [createdBooking, ...concurrent].sort(compareBookingAge)[0];
        if (keeper?.id !== createdBooking.id) {
          try {
            await client.graphql({
              query: customUpdateBooking,
              variables: {
                input: {
                  id: createdBooking.id,
                  _version: createdBooking._version,
                  deleted: '1',
                  status: BookingStatus.CANCELLED,
                  overallStatus: BookingStatus.CANCELLED,
                  statusUpdatedByID: userID,
                  statusUpdatedByName: `${userName} (duplicate booking undone)`,
                  statusUpdatedAt: new Date().toISOString(),
                },
              },
              authMode: 'userPool',
            });
          } catch (rollbackErr) {
            console.error('Could not undo the duplicate booking:', rollbackErr);
            throw new Error(
              `${DUPLICATE_GUEST_BOOKING_MESSAGE} A duplicate booking (${createdBooking.id}) was created and could not be undone automatically; please cancel it.`,
            );
          }
          throw new Error(
            `${data.guestName ? `${data.guestName}: ` : ''}${DUPLICATE_GUEST_BOOKING_MESSAGE}`,
          );
        }
      }

      if (createdBooking?.id && createdBooking?.bookingGuestId) {
        syncBookingOwnersInBackground([createdBooking.id]);
      }

      await createWorkflowTimeline({
        actionName: 'Booking created with ticket expiration',
        oldStatus: 'N/A',
        newStatus: createdBooking?.status || data.status || 'pending',
        bookingId: createdBooking?.id,
        customerId:
          createdBooking?.bookingGuestId ||
          createdBooking?.bookingMainGuestId ||
          data.bookingGuestId ||
          data.bookingMainGuestId,
        description: `Booking created with expiration ${createInput.paymentExpirationAt || 'not-set'}`,
        userID,
        userName,
      });
      // showConfirm(`New ${singleName} has been created successfully`);
      return createdBooking;
    } catch (err) {
      // this is special case for bookings and booking tickets only
      // showError(err);
      throw err;
    }
  }

  async function update(
    params: BookingUpdateVariables & {
      // Group approve ("Approve All"): no main-guest notification from this member's
      // update; the caller sends ONE for the whole group afterwards.
      skipGroupNotifications?: boolean;
      // Rows the caller just saved (GSI list reads right after a write can be stale)
      freshGroupRows?: any[];
      // The caller's status timeline entry ("Approved Booking" / "Rejected Booking"):
      // written right after the status update succeeds, before (and independent of)
      // the emails / SMS, so a failing notification can't drop it.
      statusTimeline?: {
        actionName: string;
        oldStatus?: string | null;
        newStatus: string;
        customerId?: string | null;
        description?: string;
      };
    },
  ) {
    const { id, listing, data, userID, userName, ticketChanged } = params;
    const suppressGroupNotifications = !!params.skipGroupNotifications;
    const freshGroupRows: any[] = (params.freshGroupRows || []).filter((r: any) => r?.id);

    try {
      const original: any = await get({ id, listing: [] });
      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }
      const isReApproved =
        original.status === BookingStatus.REJECTED &&
        data.status === BookingStatus.APPROVED;
      // Accepting / rejecting a guest transfer puts a paid ticket back to approved:
      // not an approval, so no new payment window or "complete your payment" messages.
      const isTransferResolution = original.status === BookingStatus.TRANSFERED;
      const isStatusMovingToApproved =
        data.status === BookingStatus.APPROVED &&
        original.status !== BookingStatus.APPROVED &&
        !isTransferResolution;

      // One live booking per guest and event: every approve / re-approve path (booking
      // page, group approve, list row) goes through here. Transfers are not approvals.
      // Matched by guest id AND phone (any format): phone-only / not registered rows have
      // no guest id.
      if (
        isStatusMovingToApproved &&
        (original.bookingGuestId || original.phone_number || original.guest?.phone_number) &&
        original.bookingEventId &&
        original.status !== BookingStatus.TRANSFERED &&
        !original.isPendingTransfer &&
        !data.isPendingTransfer
      ) {
        const guestEventBookings = await fetchGuestEventBookingsForConflict(
          original.bookingEventId,
          original.bookingGuestId,
          [original.phone_number, original.guest?.phone_number],
        );
        if (findConflictingGuestBooking(original, guestEventBookings)) {
          throw new Error(
            `${original.guest?.name ? `${original.guest.name}: ` : ''}${DUPLICATE_GUEST_BOOKING_MESSAGE}`,
          );
        }
      }

      const targetEventTicketId =
        data.bookingEventTicketId || original.bookingEventTicketId;
      let fixedTicketExpirationAt: string | undefined;
      let fixedTicketExpirationDays: number | undefined;
      let fixedTicketExpirationHours: number | undefined;
      let fixedTicketExpirationUnit: string | undefined;
      // The (possibly new) ticket: the update mutation returns no eventTicket
      let targetTicket: any = null;
      if (targetEventTicketId) {
        const eventTicketRes: any = await client.graphql({
          query: getEventTicket,
          variables: { id: targetEventTicketId },
          authMode: 'userPool',
        });
        const ticket = eventTicketRes?.data?.getEventTicket;
        targetTicket = ticket || null;
        fixedTicketExpirationAt = ticket?.fixedPaymentExpirationAt || undefined;
        const rawDays = ticket?.fixedPaymentExpirationDays;
        fixedTicketExpirationDays = rawDays === 0 || rawDays ? Number(rawDays) : undefined;
        const rawHours = ticket?.fixedPaymentExpirationHours;
        fixedTicketExpirationHours =
          rawHours === 0 || rawHours ? Number(rawHours) : undefined;
        fixedTicketExpirationUnit = ticket?.fixedPaymentExpirationUnit || undefined;
      }

      // Payment window: an expiry the admin passes in the same action always wins. An
      // approval gets a fresh window from the ticket config when the booking becomes
      // approved (incl. rejected -> approved after an auto-reject), its ticket changes,
      // or its stored expiry is missing/past. Re-saving an already-approved booking keeps
      // its window (and the link already sent). Written in the SAME update as the status.
      const originalExpiryTs = (original as any).paymentExpirationAt
        ? new Date((original as any).paymentExpirationAt).getTime()
        : NaN;
      const hasLiveOriginalExpiry =
        !Number.isNaN(originalExpiryTs) && originalExpiryTs > Date.now();
      const needsFreshApprovalWindow =
        data.status === BookingStatus.APPROVED &&
        (isStatusMovingToApproved || !!ticketChanged || !hasLiveOriginalExpiry);
      const requestedPaymentExpirationAt =
        (data as any).paymentExpirationAt ||
        (needsFreshApprovalWindow
          ? resolveApprovalExpiryFromConfig({
            fixedPaymentExpirationUnit: fixedTicketExpirationUnit,
            fixedPaymentExpirationDays: fixedTicketExpirationDays,
            fixedPaymentExpirationHours: fixedTicketExpirationHours,
            fixedPaymentExpirationAt: fixedTicketExpirationAt,
          }, original.guest?.name || (original as any).guestName) || buildPaymentExpiryIso()
          : (original as any).paymentExpirationAt);

      // DB02: with Auto Rejection on, an unpaid booking is rejected when its wave ends,
      // so the payment deadline can't be later than the wave end; an ended wave can't
      // be approved (or get a new expiry) - its ticket/wave must be changed first.
      const waveEndTs =
        (data.status || original.status) === BookingStatus.APPROVED &&
        original.isPaid !== true
          ? getAutoRejectWaveEndTs({
            published: original.event?.published,
            autoRejectionEnabled: original.event?.autoRejectionEnabled,
            waves: targetTicket?.waves || original.eventTicket?.waves,
            waveId: data.waveId || original.waveId,
          })
          : null;
      if (
        waveEndTs !== null &&
        waveEndTs <= Date.now() &&
        (isStatusMovingToApproved || !!ticketChanged || !!(data as any).paymentExpirationAt)
      ) {
        throw new Error(
          `${original.guest?.name ? `${original.guest.name}: ` : ''}${WAVE_ENDED_APPROVAL_MESSAGE}`,
        );
      }
      // Only a new window (approval / ticket change / admin-set expiry) is capped, so a
      // plain re-save of an approved booking doesn't change the link already sent.
      const paymentExpirationAt =
        waveEndTs !== null &&
        waveEndTs > Date.now() &&
        (needsFreshApprovalWindow || !!(data as any).paymentExpirationAt)
          ? capExpiryToWaveEnd(requestedPaymentExpirationAt, waveEndTs)
          : requestedPaymentExpirationAt;

      const isExpirationChanged =
        !!paymentExpirationAt &&
        paymentExpirationAt !== (original as any).paymentExpirationAt;
      const paymentLinkUpdatedAt =
        data.status === BookingStatus.APPROVED || isExpirationChanged
          ? new Date().toISOString()
          : (original as any).paymentLinkUpdatedAt;
      // overallStatus written with the status (lists show it): a companion's follows its
      // own status; a rejected main booking rejects the whole group. Otherwise (main
      // approval) the caller passes the group's value (computeGroupOverallStatus).
      const isApproveOrReject =
        data.status === BookingStatus.APPROVED ||
        data.status === BookingStatus.REJECTED;
      const defaultOverallStatus =
        isApproveOrReject && !original.isMainGuest
          ? data.status
          : data.status === BookingStatus.REJECTED && original.isMainGuest
            ? BookingStatus.REJECTED
            : original!.overallStatus;
      const updateInput: UpdateBookingInput = {
        id: original.id,
        status: data.status ? data.status : original!.status,
        // always lowercase (lists filter on the exact value)
        overallStatus: toLowerStatus(
          data.overallStatus ? data.overallStatus : defaultOverallStatus,
        ),
        deleted: data.deleted ? data.deleted : original!.deleted,
        bookingEventId: data.bookingEventId
          ? data.bookingEventId
          : original!.bookingEventId,
        bookingGuestId: data.bookingGuestId
          ? data.bookingGuestId
          : original!.bookingGuestId,
        bookingMainGuestId: data.bookingMainGuestId
          ? data.bookingMainGuestId
          : original!.bookingMainGuestId,
        isMainGuest: data.isMainGuest
          ? data.isMainGuest
          : original!.isMainGuest,
        bookingEventTicketId: data.bookingEventTicketId
          ? data.bookingEventTicketId
          : original!.bookingEventTicketId,
        waveId: data.waveId ? data.waveId : original!.waveId,
        wave: data.waveName ? data.waveName : original!.wave,
        orderId: data.orderId ? data.orderId : original!.orderId,
        statusUpdatedByID: data.status ? userID : original.statusUpdatedByID,
        statusUpdatedByName: data.status
          ? userName
          : original.statusUpdatedByName,
        statusUpdatedAt: data.status
          ? new Date().toISOString()
          : original.statusUpdatedAt,
        bookingRejectionReasonId:
          data.rejectionReasonId || original.bookingRejectionReasonId,
        rejectionComment: data.rejectionComment || original.rejectionComment,
        balance: data.balance ? data.balance : original!.balance ?? 0,
        isPendingTransfer:
          data.isPendingTransfer !== null &&
            data.isPendingTransfer !== undefined
            ? data.isPendingTransfer
            : original.isPendingTransfer,
        lastGuestId:
          data.lastGuestId !== null && data.lastGuestId !== undefined
            ? data.lastGuestId
            : original.lastGuestId,
        lastMainGuestId:
          data.lastMainGuestId !== null && data.lastMainGuestId !== undefined
            ? data.lastMainGuestId
            : original.lastMainGuestId,
        isTransfered:
          data.isTransfered !== null && data.isTransfered !== undefined
            ? data.isTransfered
            : original.isTransfered,
        guestName:
          data.guestName !== null && data.guestName !== undefined
            ? data.guestName
            : original.guestName,
        ownerHistory:
          data.ownerHistory !== null && data.ownerHistory !== undefined
            ? data.ownerHistory
            : original.ownerHistory,
        _version: original._version,
      };
      // Only when given (e.g. a rejected transfer restores the sender's phone)
      if (data.phone_number) (updateInput as any).phone_number = data.phone_number;
      (updateInput as any).paymentExpirationAt = paymentExpirationAt;
      (updateInput as any).paymentLinkUpdatedAt = paymentLinkUpdatedAt;
      if (isExpirationChanged || isStatusMovingToApproved) {
        // New payment window: restart the reminder clock
        (updateInput as any).expirationReminderSentAt = null;
        (updateInput as any).expirationReminderTargetAt = null;
      }

      // Update Query using custom mutation to avoid requesting problematic guest/mainGuest fields
      let updated: any = await client.graphql({
        query: customUpdateBooking,
        variables: { input: updateInput },
        authMode: 'userPool',
      });
      let selectedBooking = updated.data.updateBooking;
      if (params.statusTimeline && selectedBooking?.id) {
        try {
          await client.graphql({
            query: createTimeline,
            variables: {
              input: {
                bookingId: selectedBooking.id,
                actionName: params.statusTimeline.actionName,
                oldStatus: params.statusTimeline.oldStatus ?? original.status,
                newStatus: params.statusTimeline.newStatus,
                customerId:
                  params.statusTimeline.customerId ||
                  original.bookingGuestId ||
                  original.bookingMainGuestId ||
                  '',
                description: params.statusTimeline.description,
                deleted: '0',
                createdAt: new Date().toISOString(),
                createdByID: userID,
                createdByName: userName,
              },
            },
            authMode: 'userPool',
          });
        } catch (statusTimelineErr) {
          console.error(
            `Failed to create timeline "${params.statusTimeline.actionName}":`,
            statusTimelineErr,
          );
        }
      }
      // The ticket changed hands (e.g. a rejected transfer restores the sender): move the
      // owner of its live updates too.
      if (
        selectedBooking?.id &&
        (updateInput.bookingGuestId || null) !== (original.bookingGuestId || null)
      ) {
        syncBookingOwnersInBackground([selectedBooking.id]);
      }

      const isStatusChanging = data.status && data.status !== original.status;

      // Create timeline for expiration change if applicable
      if (paymentExpirationAt !== original.paymentExpirationAt && !isStatusChanging) {
        try {
          const formateDate = (date: string) => {
            const formattedDate = new Date(date);
            return formattedDate.toLocaleString('en-gp');
          };

          const oldExp = original.paymentExpirationAt
            ? formateDate(original.paymentExpirationAt)
            : 'Not set';
          const newExp = paymentExpirationAt
            ? formateDate(paymentExpirationAt)
            : 'Cleared';

          let description = `Payment expiration updated from ${oldExp} to ${newExp}`;

          if (paymentExpirationAt) {
            const diffMs = new Date(paymentExpirationAt).getTime() - Date.now();
            const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
            const hours = Math.round(diffMs / (1000 * 60 * 60));

            if (days >= 1) {
              description += ` (${days} day${days > 1 ? 's' : ''})`;
            } else if (hours >= 1) {
              description += ` (${hours} hour${hours > 1 ? 's' : ''})`;
            }
          }

          if (isStatusMovingToApproved) {
            description += ` (Auto-set on Approval)`;
          }

          if (!isReApproved) {
            await client.graphql({
              query: createTimeline,
              variables: {
                input: {
                  bookingId: original.id,
                  actionName: 'Updated Payment Expiration',
                  description: description,
                  oldStatus: original.status,
                  newStatus: data.status || original.status,
                  createdByID: userID,
                  createdByName: userName,
                  customerId:
                    original.bookingGuestId || original.bookingMainGuestId || '',
                },
              },
              authMode: 'userPool',
            });
          }

          // Automatic notification for expiration update. Not for an approval action
          // (status: approved, e.g. re-save / ticket change): the approval path below
          // sends its own payment link, so this would be a duplicate message.
          if (
            selectedBooking.isMainGuest &&
            original.status === BookingStatus.APPROVED &&
            data.status !== BookingStatus.APPROVED &&
            !data.isPendingTransfer
          ) {
            const mainGuest = original.guest;
            const event = original.event;
            // Group payment rule: no payment link while a group member is undecided
            let groupHasUndecided = false;
            if (
              mainGuest &&
              (event?.sendEmailToValidGuest || event?.sendSmsToValidGuest)
            ) {
              const groupMembers = await fetchBookingsByMainBookingId(
                selectedBooking.id,
                { deleted: { eq: '0' } },
              );
              groupHasUndecided = groupMembers.some((b: any) =>
                isUndecidedStatus(b.status),
              );
            }
            if (
              groupHasUndecided &&
              event?.sendSmsToValidGuest &&
              mainGuest?.phone_number
            ) {
              const expirationDateStr = paymentExpirationAt
                ? formatPaymentDeadline(paymentExpirationAt)
                : 'N/A';
              const smsContent = `Hi ${mainGuest.name || 'Guest'}, your payment expiration for "${event?.name || 'your event'}" has been updated. It now expires on ${expirationDateStr}. Payment is on hold until all guests in your group are approved or rejected; you will then receive the payment link.`;
              await sendSmsToUser(mainGuest.phone_number, smsContent);
            } else if (
              !groupHasUndecided &&
              mainGuest &&
              (event?.sendEmailToValidGuest || event?.sendSmsToValidGuest)
            ) {
              const expirationDateStr = paymentExpirationAt
                ? formatPaymentDeadline(paymentExpirationAt)
                : 'N/A';
              const paymentLinkEmail = buildPaymentLink(
                selectedBooking.id,
                PaymentLinkOrigin.EMAIL,
                paymentExpirationAt,
              );
              const paymentLinkSms = buildPaymentLink(
                selectedBooking.id,
                PaymentLinkOrigin.SMS,
                paymentExpirationAt,
              );
              const eventName = event?.name || 'your event';
              const guestName = mainGuest.name || 'Guest';

              if (event?.sendEmailToValidGuest && mainGuest.email) {
                const summary = await buildGroupPaymentSummary(
                  { ...original, ...selectedBooking },
                  [selectedBooking, ...freshGroupRows],
                );
                await sendEmailSafe({
                  customerEmail: mainGuest.email,
                  eventName: eventName,
                  guestName: mainGuest.name,
                  link: paymentLinkEmail,
                  expirationDate: expirationDateStr,
                  amount: summary.amount,
                  ticketsCount: summary.ticketsCount,
                  templateName: 'UlterPaymentReminder',
                });
              }
              if (event?.sendSmsToValidGuest && mainGuest.phone_number) {
                const smsContent = `Hi ${guestName}, your payment expiration for "${eventName}" has been updated. It now expires on ${expirationDateStr}. Please complete your payment here: ${paymentLinkSms}`;
                await sendSmsToUser(mainGuest.phone_number, smsContent);
              }
            }
          }
        } catch (timelineErr) {
          console.error('Failed to create expiration timeline:', timelineErr);
        }
      }
      // Status timeline entries come from the caller (params.statusTimeline, written above
      // right after the status update) to avoid duplicate records on reject/approve.

      // Update Listing
      // Fresh store listing: several updates can run in parallel (group
      // approve/reject) and the render-time listing may already be stale.
      const updatedBookingListing = [
        ...((store.getState() as any)?.bookings?.listing ?? bookingsListing ?? []),
      ];
      const updatedBookingIdx = updatedBookingListing.findIndex(
        (booking) => booking.id === selectedBooking.id,
      );
      if (updatedBookingIdx >= 0) {
        // Merge: the mutation returns flat fields only (no guest/mainGuest/event), and
        // the list row needs those to render.
        updatedBookingListing[updatedBookingIdx] = {
          ...updatedBookingListing[updatedBookingIdx],
          ...selectedBooking,
        };
        dispatch(setListing(updatedBookingListing));
      }
      patchListResultRows([selectedBooking]);
      if (
        data.status === BookingStatus.APPROVED &&
        !data.isPendingTransfer &&
        !isTransferResolution
      ) {
        try {
          const shouldSendEmail = original?.event?.sendEmailToValidGuest ?? true;
          const shouldSendSms = original?.event?.sendSmsToValidGuest ?? true;
          if (ticketChanged) {
            if (shouldSendSms) {
              const mainExpStr = paymentExpirationAt
                ? formatPaymentDeadline(paymentExpirationAt)
                : 'N/A';
              // No payment link while another member of the group is still undecided
              const tcMainBookingId = original.isMainGuest
                ? original.id
                : original.mainBookingId && original.mainBookingId !== '0'
                  ? original.mainBookingId
                  : null;
              let tcGroupHasUndecided = false;
              if (tcMainBookingId) {
                const tcMembers = await fetchBookingsByMainBookingId(tcMainBookingId, {
                  deleted: { eq: '0' },
                });
                tcGroupHasUndecided = tcMembers.some(
                  (b: any) => b.id !== selectedBooking.id && isUndecidedStatus(b.status),
                );
              }
              if (original.isMainGuest && tcGroupHasUndecided) {
                const textContent = `Hi ${original.guest.name}, ${original.eventTicket.type} tickets are not available, your booking for "${original.event?.name}" is approved with ${(selectedBooking.eventTicket || targetTicket)?.type} tickets. Payment is currently on hold as some registered guests are still pending approval. Once all guests are either approved or rejected, you will receive another SMS with the payment link.`;
                await sendSmsToUser(original.guest.phone_number, textContent);
              } else if (original.isMainGuest) {
                const payLink = buildPaymentLink(
                  original.id,
                  PaymentLinkOrigin.SMS,
                  paymentExpirationAt,
                );
                const textContent = `Hi ${original.guest.name}, ${original.eventTicket.type} tickets are not available, please proceed with your approved ${(selectedBooking.eventTicket || targetTicket)?.type} tickets. Pay here: ${payLink}. This link expires on ${mainExpStr}.`;
                await sendSmsToUser(original.guest.phone_number, textContent);
                if (shouldSendEmail && original.guest?.email) {
                  const summary = await buildGroupPaymentSummary(
                    { ...original, ...selectedBooking },
                    [selectedBooking, ...freshGroupRows],
                    targetTicket?.waves || [],
                  );
                  await sendEmailSafe({
                    customerEmail: original.guest.email,
                    eventName: original.event?.name,
                    guestName: original.guest?.name,
                    link: buildPaymentLink(
                      original.id,
                      PaymentLinkOrigin.EMAIL,
                      paymentExpirationAt,
                    ),
                    expirationDate: mainExpStr,
                    amount: summary.amount,
                    ticketsCount: summary.ticketsCount,
                    // Approved now (first approval / re-approval) = "Request Approved";
                    // a ticket swap on an already approved booking = reminder
                    templateName: isStatusMovingToApproved
                      ? 'UlterApprovedReserved'
                      : 'UlterPaymentReminder',
                  });
                }
              } else if (original.mainBookingId) {
                const mainForSms = await get({ id: original.mainBookingId, listing: [] });
                const mainForSmsCanPay =
                  mainForSms?.status === BookingStatus.APPROVED && !tcGroupHasUndecided;
                if (
                  mainForSms?.guest?.phone_number &&
                  mainForSms?.status === BookingStatus.APPROVED &&
                  !mainForSmsCanPay
                ) {
                  // Main guest approved but the group is still being reviewed: no link yet
                  const textContent = `Hi ${mainForSms.guest.name}, a group member's ticket type was updated: ${original.eventTicket.type} is not available; that guest is now on ${(selectedBooking.eventTicket || targetTicket)?.type}. Payment is on hold until the rest of the group is processed.`;
                  await sendSmsToUser(mainForSms.guest.phone_number, textContent);
                } else if (mainForSms?.guest?.phone_number && mainForSmsCanPay) {
                  const mainExp = (mainForSms as any)?.paymentExpirationAt;
                  const mainExpStrResolved = mainExp
                    ? formatPaymentDeadline(mainExp)
                    : 'N/A';
                  const payLink = buildPaymentLink(
                    mainForSms.id,
                    PaymentLinkOrigin.SMS,
                    mainExp,
                  );
                  const textContent = `Hi ${mainForSms.guest.name}, a group member's ticket type was updated: ${original.eventTicket.type} is not available; that guest is now on ${(selectedBooking.eventTicket || targetTicket)?.type}. Please use your group payment link for the updated total: ${payLink}. This link expires on ${mainExpStrResolved}.`;
                  await sendSmsToUser(mainForSms.guest.phone_number, textContent);
                  if (shouldSendEmail && mainForSms?.guest?.email) {
                    const summary = await buildGroupPaymentSummary(mainForSms, [
                      selectedBooking,
                    ]);
                    await sendEmailSafe({
                      customerEmail: mainForSms.guest.email,
                      eventName: mainForSms.event?.name || original.event?.name,
                      guestName: mainForSms.guest?.name,
                      link: buildPaymentLink(
                        mainForSms.id,
                        PaymentLinkOrigin.EMAIL,
                        mainExp,
                      ),
                      expirationDate: mainExpStrResolved,
                      amount: summary.amount,
                      ticketsCount: summary.ticketsCount,
                      templateName: 'UlterPaymentReminder',
                    });
                  }
                }
              }
            }
          } else {
            // Resolve the main guest's booking reliably:
            // 1. Determine the main guest's GUEST id (not booking id).
            // 2. Fetch all bookings for that guest in the same event.
            // 3. Pick the one with isMainGuest === true.
            const mainGuestGuestId = original.bookingMainGuestId || original.bookingGuestId;
            const eventId = original.bookingEventId;
            let hasPending = false;
            let allBookings: any[] = [];
            let mainGuestBooking: any = null;

            const hasMainBookingId =
              original.mainBookingId && original.mainBookingId !== '0';
            if (original.isMainGuest) {
              // The edited booking IS the main booking (never look up another main booking
              // of the same guest, e.g. an old rejected group). It was just approved.
              mainGuestBooking = { ...original, status: BookingStatus.APPROVED };
            } else if (hasMainBookingId) {
              // Older records can miss bookingMainGuestId; resolve main booking directly.
              mainGuestBooking = await get({
                id: original.mainBookingId,
                listing: [],
              });
            }

            if (!mainGuestBooking && mainGuestGuestId && eventId) {
              // Fetch main guest's bookings in this event
              const mainGuestBookings = await fetchBookingsByGuestId(
                mainGuestGuestId,
                {
                  bookingEventId: { eq: eventId },
                  deleted: { eq: '0' },
                },
              );
              mainGuestBooking = pickMainGuestBooking(
                mainGuestBookings,
                original.orderId,
              );
            }

            // Also get all group members via mainBookingId (if main booking found)
            const mainBookingId = mainGuestBooking?.id || original.mainBookingId;
            if (mainBookingId && mainBookingId !== '0') {
              const bookingList = await fetchBookingsListByMainBookingId(
                { deleted: { eq: '0' } },
                mainBookingId,
                undefined,
              );
              allBookings = bookingList?.items || [];
            }
            // Ensure main guest booking itself is in the list (deduped)
            if (mainGuestBooking) {
              allBookings = allBookings.filter(
                (b: any) => b.id !== mainGuestBooking.id,
              );
              allBookings.push(mainGuestBooking);
            }
            if (freshGroupRows.length) {
              const freshById = new Map<string, any>(freshGroupRows.map((r: any) => [r.id, r]));
              allBookings = allBookings.map((b: any) =>
                freshById.has(b.id) ? { ...b, ...freshById.get(b.id) } : b,
              );
              if (mainGuestBooking && freshById.has(mainGuestBooking.id)) {
                mainGuestBooking = { ...mainGuestBooking, ...freshById.get(mainGuestBooking.id) };
              }
            }

            // Undecided = pending / partially approved / not registered (same set as the
            // payment Lambda); cancelled/banned/transfered members don't hold the payment.
            hasPending = allBookings.some((booking: Booking) => {
              const currentStatus =
                booking.id === selectedBooking.id
                  ? BookingStatus.APPROVED
                  : booking.status;
              return isUndecidedStatus(currentStatus);
            });

            // Phase 3 requirement: immediate main-guest notification when a member is approved (from rejected or pending)
            // if the main guest is already approved and has a payment link.
            const isMainGuestApproved = mainGuestBooking?.status === BookingStatus.APPROVED;
            // Only a real status change (pending/rejected -> approved) notifies: re-saving
            // an already approved member must not send the main guest another email.
            const shouldSendGroupUpdateNotification =
              !suppressGroupNotifications &&
              (isReApproved || (isStatusMovingToApproved && !original.isMainGuest)) &&
              isMainGuestApproved &&
              !hasPending;

            // The payment Lambda only accepts a group payment through an APPROVED main
            // booking, so a payment link to a rejected main guest would not work. Tell the
            // admin instead; approving the main guest sends the refreshed group link.
            if (
              !original.isMainGuest &&
              (isReApproved || data.status === BookingStatus.APPROVED) &&
              mainGuestBooking?.status === BookingStatus.REJECTED
            ) {
              showWarning(
                `${original.guest?.name || 'This guest'} is approved, but the main guest's booking${
                  mainGuestBooking.guest?.name ? ` (${mainGuestBooking.guest.name})` : ''
                } is rejected, so they can't pay for the group. Approve the main guest's booking to send them the group payment link.`,
              );
            }

            // Expiry carried by any payment link sent below (must equal the main booking's
            // stored paymentExpirationAt, which the payment Lambda / page compare with `e`).
            let mainLinkExpiry: string | undefined = original.isMainGuest
              ? selectedBooking?.paymentExpirationAt || paymentExpirationAt
              : mainGuestBooking?.paymentExpirationAt || undefined;

            // Main/solo approval with a new window (and no group-update block below, which
            // aligns itself): give already-approved, unpaid companions the same window so
            // none of them is "expired" when the group pays.
            // Also after a group approve (freshGroupRows): members approved just before
            // the main booking get the main booking's window.
            if (
              original.isMainGuest &&
              (isExpirationChanged || freshGroupRows.length > 0) &&
              mainLinkExpiry &&
              !shouldSendGroupUpdateNotification
            ) {
              try {
                const failedMembers = await alignGroupPaymentWindow(
                  original.id,
                  mainLinkExpiry,
                  freshGroupRows,
                );
                if (failedMembers) {
                  showWarning(
                    `The main guest is approved, but ${failedMembers} group member(s) did not get the new payment window. Set their expiration from the main guest's booking.`,
                  );
                }
              } catch (alignErr) {
                console.error('Failed to align group payment window:', alignErr);
                showWarning(
                  "The main guest is approved, but the group members' payment window could not be updated. Set their expiration from the main guest's booking.",
                );
              }
            }

            if (shouldSendGroupUpdateNotification && mainGuestBooking) {
              let mainPaymentExp: string | undefined;
              if (original.isMainGuest) {
                // The main/solo booking was just approved by the update above, which
                // already wrote the fresh window (admin-chosen or ticket config) and a new
                // paymentLinkUpdatedAt together with the status. Use those saved values;
                // a second write would override the admin's expiry and, read back with an
                // eventually consistent get, can fail on a stale _version (which used to
                // abort every notification of this approval silently).
                mainPaymentExp = selectedBooking?.paymentExpirationAt || paymentExpirationAt;
              } else {
                const mainForReset = await get({ id: mainGuestBooking.id, listing: [] });
                let mainUpdated: any = null;
                try {
                  const reset = await resetMainGuestPaymentWindow(
                    mainForReset || mainGuestBooking,
                  );
                  mainPaymentExp = reset.paymentExpirationAt;
                  mainUpdated = reset.mainUpdated;
                } catch (resetErr) {
                  console.error('Failed to reset the main guest payment window:', resetErr);
                  // Keep the main booking's stored window if it is still open, so the link
                  // we send still matches the DB; otherwise tell the admin.
                  const storedExp = (mainForReset || mainGuestBooking)?.paymentExpirationAt;
                  if (storedExp && new Date(storedExp).getTime() > Date.now()) {
                    mainPaymentExp = storedExp;
                  } else {
                    showWarning(
                      "The guest is approved, but the main guest's payment window could not be refreshed. Set a new expiration from the main guest's booking to send the payment link.",
                    );
                    throw resetErr;
                  }
                }
                if (mainUpdated?.id) {
                  const idx = updatedBookingListing.findIndex((b) => b.id === mainUpdated.id);
                  if (idx >= 0) {
                    updatedBookingListing[idx] = {
                      ...updatedBookingListing[idx],
                      ...mainUpdated,
                    };
                    dispatch(setListing([...updatedBookingListing]));
                  }
                  patchListResultRows([mainUpdated]);
                }
              }
              mainLinkExpiry = mainPaymentExp;
              // Restart the clock for the whole group, not only the main booking.
              if (mainPaymentExp) {
                try {
                  const failedMembers = await alignGroupPaymentWindow(
                    mainGuestBooking.id,
                    mainPaymentExp,
                    [selectedBooking, ...freshGroupRows],
                  );
                  if (failedMembers) {
                    showWarning(
                      `The payment window was reset for the main guest, but ${failedMembers} group member(s) could not be updated. Set their expiration from the main guest's booking.`,
                    );
                  }
                } catch (alignErr) {
                  console.error('Failed to reset group payment window:', alignErr);
                  showWarning(
                    "The payment window was reset for the main guest, but the group members' window could not be updated. Set their expiration from the main guest's booking.",
                  );
                }
              }

              const eventName = mainGuestBooking.event?.name || original.event?.name;
              const ticketType = mainGuestBooking.eventTicket?.type || original.eventTicket?.type;
              const refreshedLinkEmail = buildPaymentLink(
                mainGuestBooking.id,
                PaymentLinkOrigin.EMAIL,
                mainPaymentExp,
              );
              const refreshedLinkSms = buildPaymentLink(
                mainGuestBooking.id,
                PaymentLinkOrigin.SMS,
                mainPaymentExp,
              );

              const expirationDateStr = mainPaymentExp
                ? formatPaymentDeadline(mainPaymentExp)
                : 'N/A';

              const updateType = isReApproved
                ? 'moved from rejected to approved'
                : 'newly approved';
              const groupMemberLine = original.isMainGuest
                ? 'Your booking is approved'
                : `A member of your group (${original.guest?.name}) was ${updateType}`;

              // Email failures must not block the SMS (sendEmailSafe never throws).
              // Main/solo (re-)approval = the guest's own approval: "Request Approved"
              // email. A member approved from pending decided the last undecided seat:
              // the main guest's first payable link (they only got "on hold" so far),
              // so "Request Approved" too. A member re-approved from rejected = reminder.
              if (original.event?.sendEmailToValidGuest && mainGuestBooking.guest?.email) {
                const summary = await buildGroupPaymentSummary(
                  original.isMainGuest
                    ? { ...original, ...selectedBooking }
                    : mainGuestBooking,
                  [selectedBooking, ...freshGroupRows],
                );
                await sendEmailSafe({
                  customerEmail: mainGuestBooking.guest.email,
                  eventName,
                  guestName: mainGuestBooking.guest?.name,
                  link: refreshedLinkEmail,
                  expirationDate: expirationDateStr,
                  amount: summary.amount,
                  ticketsCount: summary.ticketsCount,
                  templateName:
                    original.isMainGuest || isUndecidedStatus(original.status)
                      ? 'UlterApprovedReserved'
                      : 'UlterPaymentReminder',
                });
              }
              if (original.event?.sendSmsToValidGuest && mainGuestBooking.guest?.phone_number) {
                const smsContent = original.isMainGuest
                  ? `Hi ${mainGuestBooking.guest.name}, your booking is approved for "${eventName}". Complete your payment to get your ${ticketType} ticket(s): ${refreshedLinkSms}. This link expires on ${expirationDateStr}.`
                  : `Hi ${mainGuestBooking.guest.name}, ${groupMemberLine} for "${eventName}". Your group payment link now includes the updated number of guests/tickets. Pay here: ${refreshedLinkSms}. This link expires on ${expirationDateStr} (timer reset from this approval).`;
                await sendSmsToUser(
                  mainGuestBooking.guest.phone_number,
                  smsContent,
                );
              }

              await createWorkflowTimeline({
                actionName: 'Group Update Notification Sent',
                oldStatus: original.status,
                newStatus: data.status,
                bookingId: mainGuestBooking.id,
                customerId:
                  mainGuestBooking?.bookingGuestId ||
                  mainGuestBooking?.bookingMainGuestId,
                description: `Main guest notified; payment window reset on main booking. Expiration ${mainPaymentExp || 'not-set'}. (${updateType}: ${original.guest?.name}).`,
                userID,
                userName,
              });
            }

            if (
              data.status === BookingStatus.APPROVED &&
              original.status !== BookingStatus.APPROVED &&
              !original.isMainGuest
            ) {
              const eventName = original.event?.name || 'your event';
              if (shouldSendSms && original.guest?.phone_number) {
                const individualSmsContent = `Hi ${original.guest.name}, your booking for "${eventName}" has been approved! Your group's main guest will be notified to complete the payment for your ticket.`;
                await sendSmsToUser(original.guest.phone_number, individualSmsContent);
              }
              // The member's own email no longer depends on the SMS setting
              if (
                original.event?.sendEmailToValidGuest &&
                original.guest?.email &&
                !hasPending
              ) {
                const summary = await buildGroupPaymentSummary(mainGuestBooking, [
                  selectedBooking, ...freshGroupRows,
                ]);
                await sendEmailSafe({
                  customerEmail: original.guest.email,
                  eventName,
                  guestName: original.guest?.name,
                  amount: summary.amount,
                  ticketsCount: summary.ticketsCount,
                  // Group link = the main booking's id + the main booking's expiry
                  // (the companion's own expiry would not match `e` on the main booking)
                  link: buildPaymentLink(
                    mainGuestBooking?.id || original.mainBookingId || original.id,
                    PaymentLinkOrigin.EMAIL,
                    mainLinkExpiry || paymentExpirationAt,
                  ),
                  expirationDate: (mainLinkExpiry || paymentExpirationAt)
                    ? formatPaymentDeadline((mainLinkExpiry || paymentExpirationAt) as string)
                    : 'N/A',
                  templateName: 'UlterApprovedReserved',
                });
              }
            }

            if (hasPending && !shouldSendGroupUpdateNotification && !suppressGroupNotifications) {
              if (original.isMainGuest) {
                const textContent = `Hi ${original.guest.name}, your booking for "${original.event.name}" is approved. Payment is currently on hold as some registered guests are still pending approval. Once all guests are either approved or rejected, you will receive another SMS with the payment link.`;
                if (original.event.sendSmsToValidGuest && original.guest?.phone_number) {
                  await sendSmsToUser(original.guest.phone_number, textContent);
                }
                if (original.event.sendEmailToValidGuest && original.guest?.email) {
                  await sendEmailSafe({
                    customerEmail: original.guest.email,
                    eventName: original.event?.name,
                    guestName: original.guest?.name,
                    link: buildPaymentLink(
                      original.id,
                      PaymentLinkOrigin.EMAIL,
                      mainLinkExpiry || paymentExpirationAt,
                    ),
                    expirationDate: (mainLinkExpiry || paymentExpirationAt)
                      ? formatPaymentDeadline((mainLinkExpiry || paymentExpirationAt) as string)
                      : 'N/A',
                    templateName: 'UlterPaymentHoldReserved',
                  });
                }
              } else if (mainGuestBooking) {
                // Notify main guest about member approval even if on hold
                const textContent = `Hi ${mainGuestBooking.guest.name}, your guest (${original.guest?.name}) is approved for "${original.event.name}". Payment is on hold until the rest of the group is processed.`;
                if (original.event.sendSmsToValidGuest && mainGuestBooking.guest?.phone_number) {
                  await sendSmsToUser(mainGuestBooking.guest.phone_number, textContent);
                }
                if (original.event.sendEmailToValidGuest && mainGuestBooking.guest?.email) {
                  await sendEmailSafe({
                    customerEmail: mainGuestBooking.guest.email,
                    eventName:
                      mainGuestBooking.event?.name || original.event?.name,
                    guestName: mainGuestBooking.guest?.name,
                    link: buildPaymentLink(
                      mainGuestBooking.id,
                      PaymentLinkOrigin.EMAIL,
                      mainLinkExpiry,
                    ),
                    expirationDate: mainLinkExpiry
                      ? formatPaymentDeadline(mainLinkExpiry)
                      : 'N/A',
                    templateName: 'UlterPaymentHoldReserved',
                  });
                }
              }
            }

            // Payment link only when the whole group is decided AND the main guest's
            // booking is approved (the payment Lambda refuses anything else). Sent for a
            // real approval of the main/solo booking (pending -> approved) or a group
            // approve with newly approved members, not for a plain re-save (duplicate).
            if (
              mainGuestBooking &&
              isMainGuestApproved &&
              (isStatusMovingToApproved || freshGroupRows.length > 0) &&
              (original.event.sendEmailToValidGuest || original.event.sendSmsToValidGuest) &&
              !hasPending &&
              !isReApproved &&
              !shouldSendGroupUpdateNotification &&
              !suppressGroupNotifications
            ) {
              if (mainGuestBooking) {
                if (original.event.sendEmailToValidGuest && mainGuestBooking.guest?.email) {
                  const summary = await buildGroupPaymentSummary(
                    original.isMainGuest
                      ? { ...original, ...selectedBooking }
                      : mainGuestBooking,
                    [selectedBooking, ...freshGroupRows],
                  );
                  await sendEmailSafe({
                    customerEmail: mainGuestBooking.guest.email,
                    eventName:
                      mainGuestBooking.event?.name || original.event?.name,
                    guestName: mainGuestBooking.guest?.name,
                    link: buildPaymentLink(
                      mainGuestBooking.id,
                      PaymentLinkOrigin.EMAIL,
                      mainLinkExpiry,
                    ),
                    expirationDate: mainLinkExpiry
                      ? formatPaymentDeadline(mainLinkExpiry)
                      : 'N/A',
                    amount: summary.amount,
                    ticketsCount: summary.ticketsCount,
                    templateName: 'UlterApprovedReserved',
                  });
                }

                if (original.event.sendSmsToValidGuest && mainGuestBooking.guest?.phone_number) {
                  const eventName =
                    mainGuestBooking.event?.name || original.event?.name;
                  const ticketType =
                    mainGuestBooking.eventTicket?.type ||
                    original.eventTicket?.type;
                  const paymentLink = buildPaymentLink(
                    mainGuestBooking.id,
                    PaymentLinkOrigin.SMS,
                    mainLinkExpiry,
                  );
                  const expirationDateStr = mainLinkExpiry
                    ? formatPaymentDeadline(mainLinkExpiry)
                    : 'N/A';
                  const subjectText = original.isMainGuest
                    ? 'your booking'
                    : 'your group booking';
                  const smsContent = `Hi ${mainGuestBooking.guest.name}, ${subjectText} is ready for "${eventName}". Please complete your payment to get your ${ticketType} ticket(s): ${paymentLink}. This link expires on ${expirationDateStr}.`;
                  await sendSmsToUser(
                    mainGuestBooking.guest.phone_number,
                    smsContent,
                  );
                }
              }
            }
          }
        } catch (err) {
          console.error('Booking approval notifications failed:', err);
        }
      }
      if (data.status === BookingStatus.REJECTED) {
        try {
          // Reason name + comment of THIS rejection (the stored ones only when the
          // admin gave neither, e.g. a re-save)
          const hasNewRejectionInput = !!(data.rejectionReasonId || data.rejectionComment);
          let rejectionMessage = hasNewRejectionInput
            ? buildRejectionMessage(data.rejectionReasonId, data.rejectionComment, original)
            : buildRejectionMessage(
              original.bookingRejectionReasonId,
              original.rejectionComment,
              original,
            );
          const shouldSendEmail =
            original?.event?.sendEmailToValidGuest ?? true;
          const shouldSendSms =
            original?.event?.sendSmsToValidGuest ?? true;
          const transitionToRejectedFromApproved =
            original.status === BookingStatus.APPROVED &&
            data.status === BookingStatus.REJECTED;

          await sendBookingRejectedNotification({
            guest: original.guest,
            eventName: original.event?.name,
            rejectionMessage,
            email: shouldSendEmail || transitionToRejectedFromApproved,
            sms: shouldSendSms || transitionToRejectedFromApproved,
          });

          const rejMainGuestGuestId = original.bookingMainGuestId || original.bookingGuestId;
          const rejEventId = original.bookingEventId;
          if (
            (rejMainGuestGuestId || (original.mainBookingId && original.mainBookingId !== '0')) &&
            rejEventId &&
            (shouldSendEmail ||
              shouldSendSms ||
              transitionToRejectedFromApproved)
          ) {
            let mainGuestBooking: any = null;
            if (original.isMainGuest) {
              // The rejected booking IS the main booking (not an old group's one)
              mainGuestBooking = { ...original, status: BookingStatus.REJECTED };
            } else if (original.mainBookingId && original.mainBookingId !== '0') {
              mainGuestBooking = await get({
                id: original.mainBookingId,
                listing: [],
              });
            }
            if (!mainGuestBooking && rejMainGuestGuestId) {
              const rejMainBookings = await fetchBookingsByGuestId(
                rejMainGuestGuestId,
                {
                  bookingEventId: { eq: rejEventId },
                  deleted: { eq: '0' },
                },
              );
              mainGuestBooking = pickMainGuestBooking(
                rejMainBookings,
                original.orderId,
              );
            }

            const rejMainBookingId = mainGuestBooking?.id || original.mainBookingId;
            let allBookings: any[] = [];
            if (rejMainBookingId) {
              const bookingList = await fetchBookingsListByMainBookingId(
                { deleted: { eq: '0' } },
                rejMainBookingId,
                undefined,
              );
              allBookings = bookingList?.items || [];
            }
            if (mainGuestBooking) {
              allBookings = allBookings.filter((b: any) => b.id !== mainGuestBooking.id);
              allBookings.push(mainGuestBooking);
            }

            // Same undecided set as the payment Lambda (pending / partially approved / not registered)
            const hasPending = allBookings.some((booking: Booking) => {
              const currentStatus =
                booking.id === selectedBooking.id
                  ? BookingStatus.REJECTED
                  : booking.status;
              return isUndecidedStatus(currentStatus);
            });

            const isMainGuestApproved = mainGuestBooking?.status === BookingStatus.APPROVED;

            let mainPaymentForLink = paymentExpirationAt;
            if (mainGuestBooking && !original.isMainGuest && isMainGuestApproved && !hasPending) {
              // The last undecided member was rejected: the group can pay now. Restart the
              // window for the main booking AND every approved, unpaid member (same as
              // the approve path), otherwise members keep their old expiry.
              const mainForPay = await get({ id: mainGuestBooking.id, listing: [] });
              try {
                const reset = await resetMainGuestPaymentWindow(mainForPay || mainGuestBooking);
                mainPaymentForLink = reset.paymentExpirationAt;
                if (reset.mainUpdated?.id) patchListingRows([reset.mainUpdated]);
              } catch (resetErr) {
                console.error('Failed to reset the main guest payment window:', resetErr);
                const storedExp = (mainForPay || mainGuestBooking)?.paymentExpirationAt;
                if (storedExp && new Date(storedExp).getTime() > Date.now()) {
                  // Keep the stored (still open) window so the link matches the DB
                  mainPaymentForLink = storedExp;
                } else {
                  showWarning(
                    "The guest is rejected, but the main guest's payment window could not be refreshed. Set a new expiration from the main guest's booking to send the payment link.",
                  );
                  throw resetErr;
                }
              }
              if (mainPaymentForLink) {
                try {
                  const failedMembers = await alignGroupPaymentWindow(
                    mainGuestBooking.id,
                    mainPaymentForLink,
                    [selectedBooking, ...freshGroupRows],
                  );
                  if (failedMembers) {
                    showWarning(
                      `The payment window was reset for the main guest, but ${failedMembers} group member(s) could not be updated. Set their expiration from the main guest's booking.`,
                    );
                  }
                } catch (alignErr) {
                  console.error('Failed to reset group payment window:', alignErr);
                  showWarning(
                    "The payment window was reset for the main guest, but the group members' window could not be updated. Set their expiration from the main guest's booking.",
                  );
                }
              }
            }
            const expirationDateStr = mainPaymentForLink
              ? formatPaymentDeadline(mainPaymentForLink)
              : 'N/A';

            // If the main guest itself is rejected, send only rejection notification,
            // never send any payment-link/update notification in this action.
            const shouldSendMainGuestPaymentUpdate =
              !original.isMainGuest && isMainGuestApproved && !hasPending;

            if (mainGuestBooking && shouldSendMainGuestPaymentUpdate) {
              if (shouldSendEmail || transitionToRejectedFromApproved) {
                try {
                  const summary = await buildGroupPaymentSummary(mainGuestBooking, [
                    selectedBooking, ...freshGroupRows,
                  ]);
                  await sendEmail({
                    customerEmail: mainGuestBooking.guest?.email,
                    eventName:
                      mainGuestBooking.event?.name || original.event?.name,
                    guestName: mainGuestBooking.guest?.name,
                    link: buildPaymentLink(
                      mainGuestBooking.id,
                      PaymentLinkOrigin.EMAIL,
                      mainPaymentForLink,
                    ),
                    expirationDate: expirationDateStr,
                    amount: summary.amount,
                    ticketsCount: summary.ticketsCount,
                    // Rejecting the last undecided (pending) member makes the group
                    // payable for the first time: "Request Approved" (the main guest only
                    // got "on hold" so far). Otherwise the group link changed: reminder.
                    templateName: isUndecidedStatus(original.status)
                      ? 'UlterApprovedReserved'
                      : 'UlterPaymentReminder',
                  });
                } catch (mainEmailErr) {
                  console.error('Failed to send the main guest payment email:', mainEmailErr);
                }
              }

              if (shouldSendSms || transitionToRejectedFromApproved) {
                const eventName = mainGuestBooking.event?.name || original.event?.name;
                const ticketType = mainGuestBooking.eventTicket?.type || original.eventTicket?.type;
                const refreshedLinkSms = buildPaymentLink(
                  mainGuestBooking.id,
                  PaymentLinkOrigin.SMS,
                  mainPaymentForLink,
                );

                let smsContent = '';
                if (isUndecidedStatus(original.status)) {
                  // The last undecided member was rejected: the group's first payable link
                  // (the main guest only got "on hold" so far), approved guests only
                  smsContent = `Hi ${mainGuestBooking.guest?.name}, your group booking for "${eventName}" has been reviewed: ${original.guest?.name || 'a guest'} was not approved. Complete your payment for the approved guest(s) to get your ${ticketType} ticket(s): ${refreshedLinkSms}. This link expires on ${expirationDateStr}.`;
                } else if (isMainGuestApproved) {
                  smsContent = `Hi ${mainGuestBooking.guest?.name}, a group member (${original.guest?.name}) was changed to rejected for "${eventName}". Your main guest payment link was refreshed; it now reflects the new group and expires on ${expirationDateStr} (timer reset): ${refreshedLinkSms}`;
                } else {
                  smsContent = `Hi ${mainGuestBooking.guest?.name}, your booking for "${eventName}" is approved, please go to your ulter account and complete your payment to get your ${ticketType} ticket ${refreshedLinkSms}. This link expires on ${expirationDateStr}.`;
                }
                if (mainGuestBooking.guest?.phone_number) {
                  try {
                    await sendSmsToUser(
                      mainGuestBooking.guest.phone_number,
                      smsContent,
                    );
                  } catch (mainSmsErr) {
                    console.error('Failed to send the main guest payment SMS:', mainSmsErr);
                  }
                }
              }
            } else if (mainGuestBooking && !original.isMainGuest && hasPending && isMainGuestApproved) {
              // Notify main guest about member rejection even if on hold
              const textContent = `Hi ${mainGuestBooking.guest.name}, a group member (${original.guest?.name}) was rejected for "${original.event.name}". Payment is on hold until the rest of the group is processed.`;
              if (original.event.sendSmsToValidGuest && mainGuestBooking.guest?.phone_number) {
                await sendSmsToUser(mainGuestBooking.guest.phone_number, textContent);
              }
              if (original.event.sendEmailToValidGuest && mainGuestBooking.guest?.email) {
                await sendEmailSafe({
                  customerEmail: mainGuestBooking.guest.email,
                  eventName: mainGuestBooking.event?.name || original.event?.name,
                  guestName: mainGuestBooking.guest?.name,
                  link: buildPaymentLink(
                    mainGuestBooking.id,
                    PaymentLinkOrigin.EMAIL,
                    paymentExpirationAt,
                  ),
                  expirationDate: paymentExpirationAt
                    ? formatPaymentDeadline(paymentExpirationAt)
                    : 'N/A',
                  templateName: 'UlterPaymentHoldReserved',
                });
              }
            }
          }
        } catch (err) {
          console.error('Booking rejection notifications failed:', err);
        }
      }
      // Rejecting the main guest rejects the whole group. Callers that already reject
      // every member themselves ("Reject all") pass skipGroupCascade.
      if (
        data.status === BookingStatus.REJECTED &&
        original.isMainGuest &&
        !params.skipGroupCascade
      ) {
        try {
          await cascadeRejectGroupMembers(
            { ...original, ...selectedBooking, guest: original.guest, event: original.event },
            {
              userID,
              userName,
              rejectionReasonId: data.rejectionReasonId,
              rejectionComment: data.rejectionComment,
              event: original.event,
            },
          );
        } catch (cascadeErr) {
          console.error('Group reject cascade failed:', cascadeErr);
          showWarning(
            'Main guest rejected, but the group members could not be rejected automatically. Please reject them manually.',
          );
        }
      }
      if (data.status === BookingStatus.REJECTED) {
        patchListingRows([selectedBooking]);
      }
      // showConfirm(`${singleName} has been updated successfully`);
      // The saved booking (mutation result over the full original, so guest/event stay),
      // for UI state: a getBooking right after the write can still return the old row.
      const savedBooking: any = { ...original, ...selectedBooking };
      if (ticketChanged && targetTicket) savedBooking.eventTicket = targetTicket;
      return savedBooking;
    } catch (err) {
      // this is special case for bookings and booking tickets only
      // showError(err);
      const normalizedMessage =
        (err as any)?.errors?.[0]?.message ||
        (err as any)?.message ||
        (err as any)?.error ||
        'Booking update failed';
      throw new Error(normalizedMessage);
    }
  }

  async function trash(params: BookingGetVariables) {
    try {
      const { id, listing } = params;

      const original = await get({ id: params.id, listing: [] });

      if (original) {
        const updateInput: UpdateBookingInput = {
          id: original.id,
          deleted: '1',
          _version: original._version,
        };

        await client.graphql({
          query: customUpdateBooking,
          variables: { input: updateInput },
          authMode: 'userPool',
        });
      }
      dispatch(setListing(listing.filter((model: any) => model.id !== id)));
      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(params: BookingBulkTrashVariables) {
    const { ids, listing } = params;

    ids.forEach(async (id: any) => {
      try {
        await trash(id);
      } catch (err) {
        throw err;
      }
    });

    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(params: BookingGetVariables) {
    const { id, listing } = params;
    try {
      await client.graphql({
        query: deleteBooking,
        variables: { input: { id } },
        authMode: 'userPool',
      });
      dispatch(setListing(listing.filter((model: any) => model.id !== id)));
      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err) {
      showError(err);
    }
  }

  const fetchBookingsListByEventTicketId = async (
    eventTicketId: string,
    nextToken: string | undefined,
  ) => {
    try {
      const filter = {
        deleted: { eq: '0' },
        bookingEventTicketId: { eq: eventTicketId },
      };
      const variables: ListBookingsQueryVariables = { filter };
      if (nextToken) {
        variables.nextToken = nextToken;
      }
      variables.limit = 100000;
      const bookingList: any = await client.graphql({
        query: listBookings,
        variables,
        authMode: 'userPool',
      });
      return bookingList.data.listBookings;
    } catch (err) {
      console.error('Error fetching bookings list:', err);
      throw err;
    }
  };

  const fetchBookingsByEventTicketId = async (eventTicketId: string) => {
    try {
      let userBookingsComplete: Booking[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const userBookingsListNew = await fetchBookingsListByEventTicketId(
          eventTicketId,
          nextToken,
        );
        userBookingsComplete.push(...userBookingsListNew.items);
        nextToken = userBookingsListNew.nextToken;
      } while (nextToken);

      return userBookingsComplete;
    } catch (err) {
      console.error('Error fetching user bookings:', err);
      throw err;
    }
  };

  const updateBookingCashlessCredit = async (data: UpdateBookingInput) => {
    try {
      const original: any = await get({ id: data.id, listing: [] });
      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }

      const input: UpdateBookingInput = {
        id: data.id,
        cashlessCredit: data.cashlessCredit,
        balance: data.balance,
        _version: original._version,
      };
      const updated: any = await client.graphql({
        query: customUpdateBooking,
        variables: { input },
        authMode: 'userPool',
      });
      return updated.data.updateBooking;
    } catch (err) {
      console.error('Error fetching bookings list:', err);
      throw err;
    }
  };

  const splitBookingsToPaidAndUnPaid = (bookings: Booking[]) => {
    const paidBookings: Booking[] = [];
    const unPaidBookings: Booking[] = [];
    for (const booking of bookings) {
      if (booking.isPaid) {
        paidBookings.push(booking);
      } else {
        unPaidBookings.push(booking);
      }
    }
    return { paidBookings, unPaidBookings };
  };

  const handleUnpaidBookings = async (
    unPaidBookings: Booking[],
    newCashlessCredit: number,
  ) => {
    try {
      const promisesArr: any[] = [];
      for (const unPaidBooking of unPaidBookings) {
        if (unPaidBooking.cashlessCredit === newCashlessCredit) continue;
        promisesArr.push(
          updateBookingCashlessCredit({
            id: unPaidBooking.id,
            cashlessCredit: newCashlessCredit,
          }),
        );
      }
      await Promise.all(promisesArr);
    } catch (err) {
    }
  };

  // const updateUserBalance = async (userId: string, creditToAdd: number) => {
  //   try {
  //     const original: Guest = await guestsGet({id: userId});
  //     const currentUserBalance = original.balance ?? 0;
  //     let newUserBalance = currentUserBalance + creditToAdd;
  //     const input: UpdateGuestInput = {
  //       id: userId,
  //       balance: newUserBalance
  //     };
  //     let updated = await client.graphql({
  //       query: updateGuest,
  //       variables: { input },
  //       authMode: 'userPool',
  //     });
  //     return updated.data.updateGuest;
  //   } catch (err) {
  //     console.error('Error updating user balance: ', err);
  //     throw err;
  //   };
  // }

  const handlePaidBookings = async (
    paidBookings: Booking[],
    newCashlessCredit: number,
  ) => {
    try {
      const promisesArr: any[] = [];
      for (const paidBooking of paidBookings) {
        const currentCashlessCredit = paidBooking.cashlessCredit ?? 0;
        const currentBalance = paidBooking.balance ?? 0;
        // check if the new cashless credit is less than or greater the last cashless credit
        // case new cashless credit is less than (decreased)
        if (currentCashlessCredit > newCashlessCredit) {
          // update the booking cashless with the new value and deduct from the balance the cashless credit difference
          const amountToBeDeducted = newCashlessCredit - currentCashlessCredit;
          promisesArr.push(
            updateBookingCashlessCredit({
              id: paidBooking.id,
              cashlessCredit: newCashlessCredit,
              balance: currentBalance + amountToBeDeducted,
            }),
          );
        } else if (currentCashlessCredit < newCashlessCredit) {
          // case new cashless credit is larger than (increased)
          // update the booking cashless with the new value and add to it the new cashless difference
          const amountToBeAdded = newCashlessCredit - currentCashlessCredit;
          promisesArr.push(
            updateBookingCashlessCredit({
              id: paidBooking.id,
              cashlessCredit: newCashlessCredit,
              balance: currentBalance + amountToBeAdded,
            }),
          );
        } else if (currentCashlessCredit === 0) {
          // just add the newCashless credit to the user and update booking
        } else {
          continue; // they are equal
        }
      }
      await Promise.all(promisesArr);
    } catch (err) {
    }
  };

  async function updateBookingsCashlessCredit(
    eventTicketId: string,
    newCashlessCredit: number,
  ) {
    try {
      // fetch all bookings with this eventTicketId
      const bookings = await fetchBookingsByEventTicketId(eventTicketId);
      // split them to paid and unPaid
      const { paidBookings, unPaidBookings } =
        splitBookingsToPaidAndUnPaid(bookings);

      await Promise.all([
        handleUnpaidBookings(unPaidBookings, newCashlessCredit),
        handlePaidBookings(paidBookings, newCashlessCredit),
      ]);
    } catch (err) {
    }
  }

  // Accepting gives the ticket to the receiver: refused when they already hold another
  // live booking for the event (or another transfer to them is pending review).
  async function assertReceiverCanGetTicket(booking: Booking) {
    if (!booking.bookingGuestId || !booking.bookingEventId) return;
    const receiverBookings = await fetchBookingsByGuestId(booking.bookingGuestId, {
      bookingEventId: { eq: booking.bookingEventId },
      deleted: { eq: '0' },
    });
    const otherPendingTransfer = (receiverBookings || []).some(
      (b: any) =>
        b?.id &&
        b.id !== booking.id &&
        b.deleted !== '1' &&
        !b._deleted &&
        b.status === BookingStatus.TRANSFERED &&
        b.isPendingTransfer === true,
    );
    if (findConflictingGuestBooking(booking, receiverBookings) || otherPendingTransfer) {
      throw new Error(
        'The receiver already has another active booking for this event. Reject or cancel that booking before accepting this transfer.',
      );
    }
  }

  async function handleMainGuestAcceptTransfer(booking: Booking) {
    try {
      await assertReceiverCanGetTicket(booking);
      // 1. fetch all other guests bookings associated with this booking and change the mainGuest id of them
      const filter = {
        bookingEventId: { eq: booking.bookingEventId },
        isMainGuest: { eq: false },
        // deleted: { eq: '0' },
      };
      const allBookingsAssociatedWithMainGuest =
        await fetchBookingsByMainGuestId(booking.lastMainGuestId!, filter);

      // 2. change the status to approved and update the owner history and the isPendingTransfer and isTransfered
      const promisesArr: any = [];
      const updateMainGuestBookingData = {
        status: BookingStatus.APPROVED,
        isPendingTransfer: false,
        isTransfered: true,
        ownerHistory: [...(booking.ownerHistory ?? []), booking.lastGuestId],
      };
      promisesArr.push(
        update({
          id: booking.id,
          data: updateMainGuestBookingData,
          userID: session.sub,
          userName: session.name,
          listing: [],
        }),
      );

      for (const notMainGuestBooking of allBookingsAssociatedWithMainGuest) {
        const updateNotMainGuestBookingData = {
          bookingMainGuestId: booking.bookingMainGuestId,
        };
        promisesArr.push(
          update({
            id: notMainGuestBooking.id,
            data: updateNotMainGuestBookingData,
            userID: session.sub,
            userName: session.name,
            listing: [],
          }),
        );
      }

      await Promise.all(promisesArr);
    } catch (err: any) {
      throw err;
    }
  }

  async function handleNotMainGuestAcceptTransfer(booking: Booking) {
    try {
      await assertReceiverCanGetTicket(booking);
      const filter = {
        isMainGuest: { eq: true },
        bookingEventId: { eq: booking.bookingEventId },
        deleted: { eq: '0' },
      };
      const mainGuestBookings = await fetchBookingsByMainGuestId(
        booking.bookingMainGuestId!,
        filter,
      );
      const mainGuestBooking = mainGuestBookings[0];
      const promisesArr: any = [];
      // 1. change the status to approved and update the owner history and the isPendingTransfer and isTransfered
      const updateNotMainGuestBookingData = {
        status: BookingStatus.APPROVED,
        isPendingTransfer: false,
        isTransfered: true,
        ownerHistory: [...(booking.ownerHistory ?? []), booking.lastGuestId],
      };
      promisesArr.push(
        update({
          id: booking.id,
          data: updateNotMainGuestBookingData,
          userID: session.sub,
          userName: session.name,
          listing: [],
        }),
      );
      // 2. update the mainGuest booking to has isPendingTransfer false
      if (mainGuestBooking) {
        promisesArr.push(
          update({
            id: mainGuestBooking.id,
            data: { isPendingTransfer: false },
            userID: session.sub,
            userName: session.name,
            listing: [],
          }),
        );
      }

      await Promise.all(promisesArr);
    } catch (err) {
      throw err;
    }
  }

  // Rejecting gives the ticket back to the sender: refused when they booked the
  // event again while the transfer was pending (they would hold two tickets).
  async function assertSenderCanGetTicketBack(booking: Booking) {
    if (!booking.lastGuestId || !booking.bookingEventId) return;
    const senderBookings = await fetchBookingsByGuestId(booking.lastGuestId, {
      bookingEventId: { eq: booking.bookingEventId },
      deleted: { eq: '0' },
    });
    if (findConflictingGuestBooking(booking, senderBookings)) {
      throw new Error(
        'The sender already has another active booking for this event. Reject or cancel that booking before rejecting this transfer.',
      );
    }
  }

  async function handleMainGuestRejectTransfer(booking: Booking) {
    try {
      await assertSenderCanGetTicketBack(booking);
      const lastGuest = await guestsGet({
        id: booking.lastGuestId,
        listing: [],
      });
      // 1. change the status to approved and update the guest and mainGuest and the isPendingTransfer and guestName
      const updateMainGuestBookingData = {
        status: BookingStatus.APPROVED,
        isPendingTransfer: false,
        bookingGuestId: booking.lastGuestId,
        bookingMainGuestId: booking.lastMainGuestId,
        guestName: lastGuest.name,
        // the transfer set the receiver's phone on the booking
        phone_number: lastGuest.phone_number,
      };
      await update({
        id: booking.id,
        data: updateMainGuestBookingData,
        userID: session.sub,
        userName: session.name,
        listing: [],
      });
    } catch (err) {
      throw err;
    }
  }

  async function handleNotMainGuestRejectTransfer(booking: Booking) {
    try {
      await assertSenderCanGetTicketBack(booking);
      const promisesArr: any = [];
      const fetchPromisesArr: any = [];
      // 1. change the status to approved and update the guest and the isPendingTransfer and guestName
      fetchPromisesArr.push(
        guestsGet({
          id: booking.lastGuestId,
          listing: [],
        }),
      );

      const filter = {
        isMainGuest: { eq: true },
        bookingEventId: { eq: booking.bookingEventId },
        deleted: { eq: '0' },
      };
      fetchPromisesArr.push(
        fetchBookingsByMainGuestId(booking.bookingMainGuestId!, filter),
      );

      const [lastGuest, mainGuestBookingList] =
        await Promise.all(fetchPromisesArr);
      const mainGuestBooking = mainGuestBookingList[0];

      const updateNotMainGuestBookingData = {
        status: BookingStatus.APPROVED,
        isPendingTransfer: false,
        bookingGuestId: booking.lastGuestId,
        guestName: lastGuest.name,
        // the transfer set the receiver's phone on the booking
        phone_number: lastGuest.phone_number,
      };
      promisesArr.push(
        update({
          id: booking.id,
          data: updateNotMainGuestBookingData,
          userID: session.sub,
          userName: session.name,
          listing: [],
        }),
      );
      // 2. update the mainGuest booking to has isPendingTransfer false
      if (mainGuestBooking) {
        promisesArr.push(
          update({
            id: mainGuestBooking.id,
            data: { isPendingTransfer: false },
            userID: session.sub,
            userName: session.name,
            listing: [],
          }),
        );
      }

      await Promise.all(promisesArr);
    } catch (err) {
      throw err;
    }
  }

  async function handleAcceptTransferBooking(booking: Booking) {
    try {
      if (booking.isMainGuest) {
        // case 1 (this booking is the mainGuest booking)
        await handleMainGuestAcceptTransfer(booking);
      } else {
        // case 2 (not mainGuest booking)
        await handleNotMainGuestAcceptTransfer(booking);
      }
      showConfirm(`${singleName} has been transfered successfully`);
      return true;
    } catch (err: any) {
      showError(err?.message ?? 'Something went wrong');
      return false;
    }
  }

  async function handleRejectTransferBooking(booking: Booking) {
    try {
      if (booking.isMainGuest) {
        // case 1 (this booking is the mainGuest booking)
        await handleMainGuestRejectTransfer(booking);
      } else {
        // case 2 (not mainGuest booking)
        await handleNotMainGuestRejectTransfer(booking);
      }
      showConfirm(`${singleName} transfer has been rejected successfully`);
      return true;
    } catch (err: any) {
      showError(err?.message ?? 'Something went wrong');
      return false;
    }
  }

  const getAdminTransferBlockReason = (
    latest: Booking,
    targetGuest: Guest,
  ): string | null => {
    if (latest.deleted && latest.deleted !== '0') {
      return 'This booking is no longer active, so it cannot be transferred.';
    }
    if (latest.status !== BookingStatus.APPROVED) {
      return 'Only an approved, active ticket can be transferred.';
    }
    if (latest.isPendingTransfer) {
      return 'Resolve the pending transfer request for this booking before using admin transfer.';
    }
    if (!latest.isPaid) {
      return 'Only paid tickets can be transferred.';
    }
    if (latest.redeemed) {
      return 'This ticket was already used at the gate (redeemed), so it cannot be transferred.';
    }
    const currentHolderId = latest.bookingGuestId;
    if (currentHolderId && currentHolderId === targetGuest.id) {
      return 'Select a different guest than the person who currently holds the ticket.';
    }
    return null;
  };

  const throwWithTransferMessage = (msg: string) => {
    showError(msg);
    const e: any = new Error(msg);
    e._transferUserMessageShown = true;
    throw e;
  };

  const graphQlErrorText = (err: unknown): string => {
    const e = err as any;
    if (!e) return '';
    if (typeof e === 'string') return e;
    if (e.message && typeof e.message === 'string') return e.message;
    if (Array.isArray(e.errors) && e.errors[0]?.message) {
      return e.errors[0].message;
    }
    if (e.errors?.[0]?.message) return e.errors[0].message;
    if (e.data && e.errors) {
      return e.errors?.[0]?.message || '';
    }
    return '';
  };

  /** Session key: source booking that was already voided by a successful admin transfer. */
  const adminTransferSessionKey = (bookingId: string) =>
    `ulter_adminTransfer_voided_${bookingId}`;

  async function handleAdminTransferBooking(
    original: Booking,
    targetGuest: Guest,
  ) {
    const userID = session.sub;
    const userName = session.name;
    const short = (id: string) => id.replace(/-/g, '').slice(0, 8);
    try {
      const latestRes: any = await client.graphql({
        query: getBooking,
        variables: { id: original.id },
        authMode: 'userPool',
      });
      const latest: Booking = latestRes?.data?.getBooking;
      if (!latest) {
        throwWithTransferMessage(
          'Booking could not be loaded. Try again or refresh the page.',
        );
      }

      const block = getAdminTransferBlockReason(latest, targetGuest);
      if (block) {
        throwWithTransferMessage(block);
      }

      const fromGuestName = latest.guestName || (latest as any).guest?.name || 'Previous holder';
      const fromBookingShort = short(latest.id);
      const eventName = (latest as any).event?.name || 'your event';

      // 1–2. Void the source ticket, create the new ticket (new id / QR) and, for a
      // main guest ticket, re-point its companions — all in ONE DynamoDB transaction
      // on the server, so either everything is saved or nothing is. The Lambda
      // re-runs every eligibility check (incl. the target guest's bookings for this
      // event) and is the source of truth; the check above is only for fast feedback.
      let newBooking: any;
      try {
        newBooking = await invokeLambda(
          LambdaFunctions.ticketingEventsOperations,
          {
            operationId: 48, // adminTransferBooking
            bookingId: latest.id,
            targetGuestId: targetGuest.id,
          },
        );
      } catch (lambdaErr: any) {
        throwWithTransferMessage(
          lambdaErr?.message ||
            'The transfer could not be completed. Refresh the page and try again.',
        );
      }
      if (!newBooking?.id) {
        throwWithTransferMessage(
          'The transfer may not have completed. Refresh this booking: if a new ticket exists for the recipient, the transfer already completed.',
        );
      }

      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(adminTransferSessionKey(latest.id), '1');
        }
      } catch (storageErr) {
        console.warn('sessionStorage for transfer flag:', storageErr);
      }

      const newShort = short(newBooking.id);
      const linkSms = `${PUBLIC_SITE_URL}/home/ticket/${newBooking.id}?origin=${PaymentLinkOrigin.SMS}`;
      const linkEmail = `${PUBLIC_SITE_URL}/home/ticket/${newBooking.id}?origin=${PaymentLinkOrigin.EMAIL}`;

      // 3–4. Timeline, SMS, email, confirm — best effort (must not fail a successful void+create)
      const toName = targetGuest.name || 'Recipient';
      const fromToLine = `From ${fromGuestName} → to ${toName}.`;

      try {
        await createWorkflowTimeline({
          actionName: 'Admin transfer completed (voided source)',
          description: `${fromToLine} Source booking is cancelled and original QR is invalid. A new pass was issued to ${toName}.`,
          oldStatus: latest.status || BookingStatus.APPROVED,
          newStatus: BookingStatus.CANCELLED,
          bookingId: latest.id,
          customerId: latest.bookingGuestId!,
          userID,
          userName,
        });
        await createWorkflowTimeline({
          actionName: 'Admin transfer completed (new ticket)',
          description: `${fromToLine} This booking was created as the new ticket and the previous owner's ticket was voided.`,
          oldStatus: '—',
          newStatus: BookingStatus.APPROVED,
          bookingId: newBooking.id,
          customerId: targetGuest.id,
          userID,
          userName,
        });
      } catch (tlErr) {
        console.error('Transfer timeline (non-fatal):', tlErr);
      }

      const smsBody = `Hi ${toName}, a ${eventName} ticket was transferred to your Ulter account. Your new pass is in your profile. View: ${linkSms}`;

      try {
        if (targetGuest.phone_number) {
          await sendSmsToUser(targetGuest.phone_number, smsBody);
        }
        if (targetGuest.email) {
          await sendEmail({
            customerEmail: targetGuest.email,
            guestName: toName,
            eventName,
            link: linkEmail,
            textContent: `Hello ${toName},\n\nA ticket for "${eventName}" has been transferred to you. Sign in to your account to see your new pass.\n\n${linkEmail}\n\n— Ulter Admin transfer`,
            // Paid-ticket template ("Get Your Ticket") — the ticket is already paid,
            // so don't use the approved/payment-request template.
            templateName: 'UlterPaymentReserved',
            adminName: userName,
          });
        }
      } catch (notifyErr) {
        console.error('Transfer recipient notification (non-fatal):', notifyErr);
        showError(
          'Ticket transferred, but the SMS/email to the new owner could not be sent. Please resend it from the booking page.',
        );
      }

      try {
        showConfirm(
          `Ticket successfully transferred: ${fromGuestName} → ${toName}.`,
        );
      } catch (cErr) {
        console.error('showConfirm (non-fatal):', cErr);
      }

      return newBooking;
    } catch (err: any) {
      if (!err?._transferUserMessageShown) {
        const detail =
          graphQlErrorText(err) ||
          (err?.message && String(err.message)) ||
          (typeof err === 'string' ? err : '');
        if (detail && String(detail).length > 0) {
          showError(detail);
        } else {
          showError(
            'The transfer may have been saved; refresh this booking. If a new ticket exists for the recipient, the transfer already completed.',
          );
        }
      }
      throw err;
    }
  }

  async function fetchBookingByIdOffline(id: string) {
    try {
      const res = await axios.get(serverAddress + '/booking/' + id);
      const data = res.data.data.data;
      return data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        throw new Error("This Guest Doesn't exist");
      } else {
        throw new Error(err?.response?.data?.message) ?? err;
      }
    }
  }

  /**
   * Bookings for an export: the list's own filters (date range, search,
   * origin, paid, status tab, ticket, wave, registered / special needs) AND
   * the export's condition. Unlike the list, companions are included (every
   * guest has a ticket). A single status (the export's or the tab's) is read
   * from the bookingsByEventAndStatus GSI instead of filtering the event.
   */
  const fetchFilteredBookingsForExport = async (
    generalFilters: any,
    exportFilter: ModelBookingFilterInput,
    exportStatus?: string,
    onProgress?: (loaded: number) => void,
  ): Promise<any[]> => {
    const bookingEventId: string = generalFilters?.event;
    if (!bookingEventId) return [];
    const tab: string =
      (store.getState() as any)?.bookings?.statusFilter ?? bookingStatusFilter;
    const { filter: listFilter, createdAtFilter } = buildBookingListFilter({
      generalFilters,
      searchText: generalFilters?.searchText,
      statusFilter: tab,
      overallStatus: tab,
    });
    const listStatus =
      typeof listFilter.status?.eq === 'string' && listFilter.status.eq !== ''
        ? listFilter.status.eq
        : undefined;
    // e.g. "Approved & not paid" while the Rejected tab is open: no booking
    // can match both.
    if (exportStatus && listStatus && exportStatus !== listStatus) return [];
    const keyStatus = exportStatus || listStatus;

    const fetchAll = async (viaStatusIndex: boolean): Promise<any[]> => {
      const listPart: ModelBookingFilterInput = { ...listFilter };
      const exportPart: ModelBookingFilterInput = { ...exportFilter };
      // The status is the key condition, or added once below.
      delete listPart.status;
      delete exportPart.status;
      const parts: ModelBookingFilterInput[] = [listPart];
      if (Object.keys(exportPart).length > 0) parts.push(exportPart);
      if (keyStatus && !viaStatusIndex)
        parts.push({ status: { eq: keyStatus } });
      // createdAt is the sort key of bookingByEventId only; on the status
      // GSI the date range is a filter.
      if (viaStatusIndex && createdAtFilter)
        parts.push({ createdAt: createdAtFilter });
      const filter: ModelBookingFilterInput =
        parts.length === 1 ? parts[0] : { and: parts };

      const rows: any[] = [];
      let nextToken: string | null | undefined = undefined;
      do {
        const variables: any = {
          bookingEventId,
          filter,
          limit: 1000,
          nextToken,
          sortDirection: ModelSortDirection.DESC,
        };
        let page: any;
        if (viaStatusIndex) {
          variables.status = { eq: keyStatus };
          const res: any = await client.graphql({
            query: bookingsByEventAndStatusExport,
            variables,
            authMode: 'userPool',
          });
          page = res?.data?.bookingsByEventAndStatus;
        } else {
          if (createdAtFilter) variables.createdAt = createdAtFilter;
          const res: any = await client.graphql({
            query: bookingByEventIdExport,
            variables,
            authMode: 'userPool',
          });
          page = res?.data?.bookingByEventId;
        }
        rows.push(...(page?.items ?? []).filter(Boolean));
        nextToken = page?.nextToken;
        onProgress?.(rows.length);
      } while (nextToken);
      return rows;
    };

    if (keyStatus && USE_STATUS_INDEX && statusIndexAvailable) {
      try {
        return await fetchAll(true);
      } catch (err) {
        if (!isUnknownStatusIndexError(err)) throw err;
        statusIndexAvailable = false;
      }
    }
    return fetchAll(false);
  };

  /**
   * Invitations for an export: the list's date range (createdAt key
   * condition), search and ticket. Booking-only filters (tab, origin, paid,
   * wave, registered / special needs) don't apply to invitations.
   */
  const fetchFilteredInvitationsForExport = async (
    generalFilters: any,
  ): Promise<any[]> => {
    const invitationEventId: string = generalFilters?.event;
    if (!invitationEventId) return [];
    const filter: any = { deleted: { eq: '0' } };
    if (generalFilters?.ticket)
      filter.invitationEventTicketId = { eq: generalFilters.ticket };
    const search = String(generalFilters?.searchText ?? '').trim();
    if (search) {
      if (/[a-zA-Z]/.test(search)) {
        const lower = search.toLowerCase();
        filter.or = [
          { name: { contains: search } },
          { name: { contains: lower } },
          { email: { contains: lower } },
        ];
      } else if (/[0-9]/.test(toAsciiDigits(search))) {
        filter.or = [{ phone_number: { contains: toPhoneSearchText(search) } }];
      }
    }
    const createdAtRange = toDayRangeIso(
      generalFilters?.fromDate,
      generalFilters?.toDate,
    );
    const rows: any[] = [];
    let nextToken: string | null | undefined = undefined;
    do {
      const variables: any = {
        invitationEventId,
        filter,
        limit: 1000,
        nextToken,
        sortDirection: ModelSortDirection.DESC,
      };
      if (createdAtRange) variables.createdAt = { between: createdAtRange };
      const res: any = await client.graphql({
        query: invitationByEventIdExport,
        variables,
        authMode: 'userPool',
      });
      const page = res?.data?.invitationByEventId;
      rows.push(...(page?.items ?? []).filter(Boolean));
      nextToken = page?.nextToken;
    } while (nextToken);
    return rows;
  };

  const generateQRCode = async (data) => {
    try {
      const QRCode = await loadQRCode();
      const qrCodeDataURL = await QRCode.toDataURL(data);
      return qrCodeDataURL;
    } catch (err) {
      console.error('Error generating QR code:', err);
      throw err;
    }
  };

  /**
   * "Paid tickets QR": paid bookings of the selected ticket that match the
   * list's filters, plus the ticket's invitations. Throws on failure (the
   * caller shows the error).
   */
  const exportingBookingsAndInvitationsQRs = async (
    generalFilters,
    onProgress?: (loaded: number) => void,
  ) => {
    if (!generalFilters.event || generalFilters.event === '') {
      showError('Select Event First');
      return;
    }
    if (!generalFilters.ticket || generalFilters.ticket === '') {
      showError('Select Ticket Type First');
      return;
    }
    const [AllBookings, AllInvitaions] = await Promise.all([
      fetchFilteredBookingsForExport(
        generalFilters,
        {
          deleted: { eq: '0' },
          bookingEventTicketId: { eq: generalFilters.ticket },
          isPaid: { eq: true },
        },
        undefined,
        onProgress,
      ),
      fetchFilteredInvitationsForExport(generalFilters),
    ]);
    await generateExcelSheet(AllBookings, AllInvitaions);
  };

  const generateExcelSheet = async (bookings, invitations) => {
    const ExcelJS = await loadExcelJS();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      'Valid Paid Bookings and Invitations',
    );

    const headers = [
      'Booking Link',
      'QR',
      'Guest Name',
      'Guest Phone',
      'Ticket Type',
      'Ticket Source',
      'Redeem Status',
    ];

    worksheet.addRow(headers);

    const columnWidths = headers.map((header) => header.length);
    const imageWidth = 100;
    const imageHeight = 100;

    worksheet.getColumn(2).width = imageWidth / 7.5;

    for (const booking of bookings) {
      const bookingLink = `${PUBLIC_SITE_URL}/?id=${booking.id}`;
      const isRedeemed = booking.redeemed ? 'Redeemed' : 'Not redeemed';
      const qrCode = await generateQRCode(bookingLink);
      const row = [
        bookingLink,
        '',
        booking?.guest?.name,
        booking?.phone_number,
        booking?.eventTicket?.type,
        booking.__typename,
        isRedeemed,
      ];
      const newRow = worksheet.addRow(row);

      worksheet.getRow(newRow.number).height = imageHeight * 0.75;

      const imageId = workbook.addImage({
        base64: qrCode,
        extension: 'png',
      });

      worksheet.addImage(imageId, {
        tl: { col: 1, row: newRow.number - 1 },
        ext: { width: imageWidth, height: imageHeight },
      });

      row.forEach((cell, index) => {
        const cellLength = cell?.toString().length;
        if (cellLength && cellLength > columnWidths[index]) {
          columnWidths[index] = cellLength;
        }
      });
    }

    for (const invitation of invitations) {
      const invitationLink = `${PUBLIC_SITE_URL}/?id=${invitation.id}`;
      const name =
        invitation.phone_number && invitation.phone_number !== ''
          ? invitation.phone_number
          : invitation.email;
      const isUsed = invitation.used ? 'Redeemed' : 'Not redeemed';
      const qrCode = await generateQRCode(invitationLink);
      const invRow = [
        invitationLink,
        '',
        name,
        invitation.phone_number,
        invitation?.eventTicket?.type,
        invitation.__typename,
        isUsed,
      ];
      const newRow = worksheet.addRow(invRow);

      worksheet.getRow(newRow.number).height = imageHeight * 0.75;

      const imageId = workbook.addImage({
        base64: qrCode,
        extension: 'png',
      });

      worksheet.addImage(imageId, {
        tl: { col: 1, row: newRow.number - 1 },
        ext: { width: imageWidth, height: imageHeight },
      });

      invRow.forEach((cell, index) => {
        const cellLength = cell?.toString().length;
        if (cellLength && cellLength > columnWidths[index]) {
          columnWidths[index] = cellLength;
        }
      });
    }

    columnWidths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const saveAs = await loadSaveAs();
    saveAs(blob, 'Tickets.xlsx');
  };

  /**
   * "Approved & not paid" / "Pending" bookings that match the list's
   * filters (status read from the bookingsByEventAndStatus GSI). Throws on
   * failure (the caller shows the error).
   */
  const exportingBookingsApprovedAndNotpaid = async (
    generalFilters,
    status,
    onProgress?: (loaded: number) => void,
  ) => {
    if (!generalFilters.event || generalFilters.event === '') {
      showError('Select Event First');
      return;
    }
    const exportStatus =
      status === 'approved'
        ? BookingStatus.APPROVED
        : status === 'pending'
          ? BookingStatus.PENDING
          : undefined;
    const AllBookings = await fetchFilteredBookingsForExport(
      generalFilters,
      { deleted: { eq: '0' }, isPaid: { ne: true } },
      exportStatus,
      onProgress,
    );
    await generateExcelSheetApprovedAndNotpaid(AllBookings);
  };

  const generateExcelSheetApprovedAndNotpaid = async (bookings) => {
    const ExcelJS = await loadExcelJS();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      'Valid Paid Bookings and Invitations',
    );

    const headers = ['Guest Name', 'Guest Email', 'Guest Phone'];

    worksheet.addRow(headers);

    const columnWidths = headers.map((header) => header.length);
    const imageWidth = 100;
    const imageHeight = 100;

    worksheet.getColumn(2).width = imageWidth / 7.5;

    for (const booking of bookings) {
      if (!booking?.guest?.email) continue;
      // (No QR on this sheet: it used to generate one per row and drop it.)
      const row = [
        // bookingLink,
        // '',
        booking?.guest?.name,
        // booking?.phone_number,
        // booking?.eventTicket.type,
        // booking.__typename,
        // isRedeemed,
        booking?.guest?.email,
        booking?.phone_number,
      ];
      const newRow = worksheet.addRow(row);

      // worksheet.getRow(newRow.number).height = imageHeight * 0.75;

      // const imageId = workbook.addImage({
      //   base64: qrCode,
      //   extension: 'png',
      // });

      // worksheet.addImage(imageId, {
      //   tl: { col: 1, row: newRow.number - 1 },
      //   ext: { width: imageWidth, height: imageHeight },
      // });

      row.forEach((cell, index) => {
        const cellLength = cell?.toString().length;
        if (cellLength && cellLength > columnWidths[index]) {
          columnWidths[index] = cellLength;
        }
      });
    }

    columnWidths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const saveAs = await loadSaveAs();
    saveAs(blob, 'Tickets.xlsx');
  };

  /**
   * "Paid sources" report: paid bookings that match the list's filters plus
   * the invitations (date range / search / ticket). Throws on failure (the
   * caller shows the error).
   */
  const exportingPaidBookingsAndInvitationsReport = async (
    generalFilters,
    onProgress?: (loaded: number) => void,
  ) => {
    if (!generalFilters.event || generalFilters.event === '') {
      showError('Select Event First');
      return;
    }
    const [AllBookings, AllInvitaions] = await Promise.all([
      fetchFilteredBookingsForExport(
        generalFilters,
        { deleted: { eq: '0' }, isPaid: { eq: true } },
        undefined,
        onProgress,
      ),
      fetchFilteredInvitationsForExport(generalFilters),
    ]);
    await generatePaidExcelSheetReport(AllBookings, AllInvitaions);
  };

  const generatePaidExcelSheetReport = async (bookings, invitations) => {
    const ExcelJS = await loadExcelJS();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      'Valid Paid Bookings and Invitations',
    );

    const headers = [
      'Guest Name',
      'Guest Phone',
      'Guest Email',
      'Ticket Type',
      'Ticket Source',
      'Booking Source',
      'Payment Type',
    ];

    worksheet.addRow(headers);

    const columnWidths = headers.map((header) => header.length);
    const imageWidth = 100;
    const imageHeight = 100;

    worksheet.getColumn(2).width = imageWidth / 7.5;

    for (const booking of bookings) {
      // const isRedeemed = booking.redeemed ? 'Redeemed' : 'Not redeemed';
      const isBooking = booking.__typename === 'Booking' ? true : false;
      const row = [
        booking?.guest?.name,
        booking?.phone_number,
        booking?.guest?.email,
        booking?.eventTicket?.type,
        booking.__typename,
        // isRedeemed,
        booking?.origin,
        isBooking && booking?.paymentMethod ? booking.paymentMethod : '-',
      ];

      const newRow = worksheet.addRow(row);

      // worksheet.getRow(newRow.number).height = imageHeight * 0.75;

      row.forEach((cell, index) => {
        const cellLength = cell?.toString().length;
        if (cellLength && cellLength > columnWidths[index]) {
          columnWidths[index] = cellLength;
        }
      });
    }

    for (const invitation of invitations) {
      const invitationLink = `${PUBLIC_SITE_URL}/?id=${invitation.id}`;
      const name =
        invitation.phone_number && invitation.phone_number !== ''
          ? invitation.phone_number
          : invitation.email;
      const isUsed = invitation.used ? 'Redeemed' : 'Not redeemed';
      const qrCode = await generateQRCode(invitationLink);
      const invRow = [
        invitationLink,
        '',
        name,
        invitation.phone_number,
        invitation?.eventTicket?.type,
        invitation.__typename,
        isUsed,
      ];
      const newRow = worksheet.addRow(invRow);

      worksheet.getRow(newRow.number).height = imageHeight * 0.75;

      const imageId = workbook.addImage({
        base64: qrCode,
        extension: 'png',
      });

      worksheet.addImage(imageId, {
        tl: { col: 1, row: newRow.number - 1 },
        ext: { width: imageWidth, height: imageHeight },
      });

      invRow.forEach((cell, index) => {
        const cellLength = cell?.toString().length;
        if (cellLength && cellLength > columnWidths[index]) {
          columnWidths[index] = cellLength;
        }
      });
    }

    columnWidths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const saveAs = await loadSaveAs();
    saveAs(blob, 'Tickets.xlsx');
  };

  const headCells: readonly HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'Name',
    },
    {
      id: 'email',
      numeric: false,
      disablePadding: false,
      label: 'Email',
    },
    {
      id: 'age',
      numeric: false,
      disablePadding: false,
      label: 'Age',
    },
    // {
    //   id: '# of tickets',
    //   numeric: false,
    //   disablePadding: false,
    //   label: '# of Tickets',
    // },
    {
      id: 'registration',
      numeric: false,
      disablePadding: false,
      label: 'Registration',
    },
    {
      id: 'group',
      numeric: false,
      disablePadding: false,
      label: 'Group',
    },
    {
      id: 'flag',
      numeric: false,
      disablePadding: false,
      label: 'Flag',
    },
    {
      id: 'average spending',
      numeric: false,
      disablePadding: false,
      label: 'Average Spending',
    },
    {
      id: '# of attended events',
      numeric: false,
      disablePadding: false,
      label: '# of Attended Events',
    },
    {
      id: 'last attended event',
      numeric: false,
      disablePadding: false,
      label: 'Last Attended Event',
    },
    {
      id: 'social',
      numeric: false,
      disablePadding: false,
      label: 'Social',
    },
    {
      id: 'special needs',
      numeric: false,
      disablePadding: false,
      label: 'Special Needs',
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: false,
      label: 'Actions',
    },
    {
      id: 'latest action',
      numeric: false,
      disablePadding: false,
      label: 'Latest Action',
    },
  ];

  const TransferHeadCells: readonly HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'Name',
    },
    {
      id: 'gender',
      numeric: false,
      disablePadding: false,
      label: 'Gender',
    },
    {
      id: 'transferTO',
      numeric: false,
      disablePadding: false,
      label: 'Transfer to',
    },
    {
      id: 'gender',
      numeric: false,
      disablePadding: false,
      label: 'Gender',
    },
    {
      id: 'requestDateTime',
      numeric: false,
      disablePadding: false,
      label: 'Request Date/Time',
    },
  ];

  const dataCells: readonly string[] = ['name', 'startDate', 'endDate'];

  const api: any = {};
  api[`${listingName}CreateSubscription`] = onCreateBooking;
  api[`${listingName}Options`] = [];
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}TransferHeadCells`] = TransferHeadCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchAllBookingsByMainGuestId`] =
    fetchAllBookingsByMainGuestId;
  api[`${listingName}FetchAllBookingsByEventId`] = fetchAllBookingsByEventId;
  api[`${listingName}FetchBookingsByGuestId`] = fetchBookingsByGuestId;
  api[`${listingName}FetchBookingsByMainGuestId`] = fetchBookingsByMainGuestId;
  api[`${listingName}FetchBookingsByEventId`] = fetchBookingsByEventId;
  api[`${listingName}FetchBookingIdsByEventId`] = fetchBookingIdsByEventId;
  api[`${listingName}HasBookingsByEventId`] = hasBookingsByEventId;
  api[`${listingName}FetchBookingsByEventIdLean`] = fetchBookingsByEventIdLean;
  api[`${listingName}FetchBookingsByMainBookingId`] =
    fetchBookingsByMainBookingId;
  api[`${listingName}ExportingBookingsAndInvitationsQRs`] =
    exportingBookingsAndInvitationsQRs;
  api[`${listingName}ExportingBookingsApprovedAndNotpaid`] =
    exportingBookingsApprovedAndNotpaid;
  api[`${listingName}ExportingPaidBookingsAndInvitationsReport`] =
    exportingPaidBookingsAndInvitationsReport;
  api[`${listingName}FetchBookings`] = fetchBookings;
  api[`${listingName}FetchBookingByIdOffline`] = fetchBookingByIdOffline;
  api[`${listingName}FetchAllBookingsByEventTicketId`] =
    getAllBookingsByEventTicketId;
  api[`${listingName}SortUserUpcomingEventsBookings`] =
    sortUserUpcomingEventsBookings;
  api[`${listingName}UpdateBookingsCashlessCredit`] =
    updateBookingsCashlessCredit;
  api[`${listingName}AcceptTransfer`] = handleAcceptTransferBooking;
  api[`${listingName}RejectTransfer`] = handleRejectTransferBooking;
  api[`${listingName}AdminTransfer`] = handleAdminTransferBooking;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}GroupPaymentSummary`] = buildGroupPaymentSummary;
  api[`${listingName}CascadeRejectGroupMembers`] = cascadeRejectGroupMembers;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ChangeListing`] = (listing: Booking[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api[`${listingName}ChangeRegisteredFilter`] = (filter: string) =>
    dispatch(setRegisteredFilter(filter));
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = bookingsListing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  return api;
};
export default useBooking;